"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { CheckCircle2, CircleAlert, CircleDashed, Clock3, ListChecks, SquareArrowOutUpRight } from "lucide-react";
import Link from "next/link";
import { getTprDepartmentName } from "@/configuration/groups/tpr/athena";
import { TprCoverageContinueButton } from "@/configuration/groups/tpr/components/coverage-continue-button";
import { TprManualStepContinueButton } from "@/configuration/groups/tpr/components/manual-step-continue-button";
import { TprOpsContinueButton } from "@/configuration/groups/tpr/components/ops-continue-button";
import { TprReceivingAssignmentContinueButton } from "@/configuration/groups/tpr/components/receiving-assignment-continue-button";
import { TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION } from "@/configuration/groups/tpr/referral-auth-instructions";
import type { StediEligibilityDisplayLabel, TprSyncChecklistTraceEntry } from "@/configuration/groups/tpr/workflows/sync-checklist";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { AthenaCoverageEvidence } from "@/src/components/tasks/approval-types/athena-coverage-evidence";
import { AthenaReferralCards, AthenaReferralCardsData } from "@/src/components/tasks/athena-referral-cards";
import { ReferralEligibilityCard } from "@/src/components/tasks/referral-eligibility-card";
import { StediEligibilitySummary } from "@/src/components/tasks/stedi-eligibility-summary";
import { SyncIncompleteActions } from "@/src/components/tasks/sync-incomplete-actions";
import { VerificationButtons } from "@/src/components/tasks/verification-buttons";
import { ValerieCard } from "@/src/components/valerie-card";
import { tprReferralAuthNeedsPendingUpload, tprReferralAuthNeedsReview } from "@/src/lib/customer/tpr/referral-auth-pending-upload";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { formatFromUnderscoreToHumanReadable } from "@/src/lib/string";
import { formatDateMmDdYyyy, formatHumanReadableDate } from "@/src/lib/utils/date-and-phone-utils";
import type { ReceivingAssignmentOutput } from "@/src/lib/workflows/steps/referral/resolve-receiving-assignment";
import type { AthenaCoverageReadbackOutput, TprInsuranceRequirementsOutput, TprReferralAuthorizationOutput } from "@/src/lib/workflows/steps/step-outputs";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { getFormattedAddress } from "@/src/models/address";
import type { Appointment } from "@/src/models/appointment";
import { Coverage, getCoverageDisplayName } from "@/src/models/coverage";
import { type DiagnosisItem, Referral } from "@/src/models/documents/referral";
import { FileType } from "@/src/models/file";
import { getPatientDisplayName, getPatientName, getPatientPhoneNumber, type Patient } from "@/src/models/patient";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import { Task, TaskStatus } from "@/src/models/task";
import { ApprovalType, type TaskApproval } from "@/src/models/task-approval";
import { type TaskStep, TaskStepStatus } from "@/src/models/task-step";
const NOT_AVAILABLE = "N/A";

// Plain string literals rather than importing the `TprSyncBucket` enum's runtime values: this is a
// "use client" component, and `sync-checklist.ts` transitively pulls in server-only Athena/GCP client
// code, so any real (non-type-only) import from it breaks the browser bundle. Values must match
// `TprSyncBucket` in configuration/groups/tpr/workflows/sync-checklist.ts exactly.
const TPR_MANUAL_REVIEW_BUCKET = "biltmorecardreferral";

/**
 * Plain-English labels for the 3 real destinations ops actually routes to. `TPR_MANUAL_REVIEW_BUCKET`
 * is deliberately excluded — it's the decision tree's "unable to auto-determine" catch-all, not a 4th
 * destination, so it gets its own "no confident suggestion" message instead of a label here.
 */
const SUGGESTED_BUCKET_LABELS: Record<string, string> = {
  closed: "Closed — upcoming appointment already scheduled",
  sscardio: "SS Cardio — stays with practice",
  biltmorecardiosched: "Biltmore — ready to schedule"
};
const NO_CONFIDENT_BUCKET_SUGGESTION = "No confident suggestion — ops must choose SS Cardio / Biltmore (ready to schedule) / Closed based on the details above.";

/** Renders provider name / NPI as independently copyable fields (no address — that's its own line now). */
function ProviderCopyFields(t0) {
  const $ = _c(9);
  const {
    provider
  } = t0;
  let t1;
  if ($[0] !== provider) {
    t1 = provider && getPractitionerNameWithCredentials(provider) || NOT_AVAILABLE;
    $[0] = provider;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const name = t1;
  const npi = provider?.npiNumber || NOT_AVAILABLE;
  let t2;
  if ($[2] !== name) {
    t2 = <CopyableContent text={name} />;
    $[2] = name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== npi) {
    t3 = <CopyableContent text={npi} />;
    $[4] = npi;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <>Name: {t2}, NPI: {t3}</>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function ResolvedProviderCopyFields(t0) {
  const $ = _c(8);
  const {
    providerLabel,
    providerNpi,
    writeSafe
  } = t0;
  const t1 = providerLabel || NOT_AVAILABLE;
  let t2;
  if ($[0] !== t1) {
    t2 = <CopyableContent text={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = providerNpi || NOT_AVAILABLE;
  let t4;
  if ($[2] !== t3) {
    t4 = <CopyableContent text={t3} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = !writeSafe && " \u2014 verify manually before entry";
  let t6;
  if ($[4] !== t2 || $[5] !== t4 || $[6] !== t5) {
    t6 = <>Name: {t2}, NPI:{" "}{t4}{t5}</>;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
function getCoverageSummary(coverage: Coverage | null | undefined): string | null {
  if (!coverage) return null;
  const display = getCoverageDisplayName({
    coverage,
    includesPlan: true,
    includeSubscriberName: true
  });
  return coverage.subscriberId ? `${display} (Member ID: ${coverage.subscriberId})` : display;
}

/** One line per past appointment for the "Patient Type" sanity-check list, most-recent-first. */
function formatAppointmentHistoryEntry(appointment: Appointment): string {
  const date = appointment.appointmentDatetime ? formatHumanReadableDate(appointment.appointmentDatetime) : "TBD";
  const type = formatFromUnderscoreToHumanReadable(appointment.type) || NOT_AVAILABLE;
  const status = formatFromUnderscoreToHumanReadable(appointment.status || "") || NOT_AVAILABLE;
  return `${date} - ${type} (${status})`;
}
function sortAppointmentsMostRecentFirst(appointments: Appointment[]): Appointment[] {
  return [...appointments].sort((a, b) => {
    const aTime = a.appointmentDatetime ? new Date(a.appointmentDatetime).getTime() : 0;
    const bTime = b.appointmentDatetime ? new Date(b.appointmentDatetime).getTime() : 0;
    return bTime - aTime;
  });
}
type DataSourceEntry = {
  label: string;
  value: string;
  source: "Referral" | "Patient Chart" | "N/A";
  missing: boolean;
};

/**
 * A field only counts as truly missing if it's absent from BOTH the referral's extracted data AND
 * the patient's existing Athena chart — an extraction miss isn't "missing" if the chart already has
 * it. Prefers the referral's value when both are present, since that's what's freshest off the fax.
 */
function resolveDataSourceEntry(label: string, referralValue: string | null, chartValue: string | null): DataSourceEntry {
  if (referralValue) return {
    label,
    value: referralValue,
    source: "Referral",
    missing: false
  };
  if (chartValue) return {
    label,
    value: chartValue,
    source: "Patient Chart",
    missing: false
  };
  return {
    label,
    value: NOT_AVAILABLE,
    source: "N/A",
    missing: true
  };
}
type TimelineStatus = "automated" | "verified" | "verify" | "needs_action" | "failed" | "waiting" | "not_run" | "not_needed";
type TimelineStep = {
  number: number;
  title: string;
  status: TimelineStatus;
  summary: string;
  taskStepKeys: StepKey[];
  content?: React.ReactNode;
  continuation?: {
    instruction: string;
    control: React.ReactNode;
  };
};
type DecisionMetadataItem = {
  label: string;
  value: React.ReactNode;
};
type TprOpsCrossCheck = {
  patientName: string;
  patientDateOfBirth: string;
  patientPhoneNumber: string;
  patientHomeAddress: string;
  receivingProviderName: string;
  receivingProviderNpi: string;
  referringProviderName: string;
  referringProviderNpi: string;
  referringProviderSpecialty: string;
  referringLocationName: string;
  referringLocationAddress: string;
  emergencyContactName: string;
  emergencyContactPhoneNumber: string;
  emergencyContactRelationship: string;
  referralDate: string;
  primaryPlanLabel: string;
  primarySubscriberId: string;
  diagnosis: string;
};
function DecisionMetadata(t0) {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <dl className="flex flex-wrap gap-2">{t1}</dl>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(item) {
  return <div key={item.label} className="inline-flex items-center gap-1.5 rounded-md border bg-muted/30 px-2 py-1 text-xs"><dt className="text-muted-foreground">{item.label}</dt><dd className="font-medium text-foreground">{item.value}</dd></div>;
}
type ProviderSelectionEvidence = NonNullable<ReceivingAssignmentOutput["providerSelectionEvidence"]>;
type ProviderSelectionStep = NonNullable<ProviderSelectionEvidence["selectedStep"]>;
function providerSelectionReason(selectedStep: ProviderSelectionEvidence["selectedStep"]): string {
  switch (selectedStep) {
    case "upcoming_appointment":
      return "Selected from the earliest qualifying Biltmore appointment within 60 days.";
    case "past_appointment":
      return "Selected from the most recent qualifying completed Biltmore appointment in the past three years.";
    case "usual_provider":
      return "Legacy selection used the Athena usual provider; verify this assignment against appointment history.";
    case "referral_provider":
      return "Selected because this provider is named on the referral.";
    case "diagnosis_match":
      return "Selected because this provider treats the referral diagnosis at the chosen location.";
    default:
      return "No provider was selected automatically.";
  }
}
function ProviderSelectionChecklist(t0) {
  const $ = _c(35);
  const {
    evidence,
    upcomingAppointmentProviderSummary,
    referralProviderName,
    referralProviderNpi,
    diagnosis,
    selectedProviderName,
    selectedLocation
  } = t0;
  let t1;
  if ($[0] !== evidence.appointmentProviders) {
    t1 = evidence.appointmentProviders.some(_temp2);
    $[0] = evidence.appointmentProviders;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasQualificationEvidence = t1;
  let t2;
  if ($[2] !== evidence.appointmentProviders || $[3] !== evidence.selectedStep || $[4] !== hasQualificationEvidence || $[5] !== upcomingAppointmentProviderSummary) {
    t2 = period => {
      const providers = evidence.appointmentProviders.flatMap(provider_0 => {
        const count = period === "past" ? provider_0.qualifyingPastAppointmentCount ?? 0 : provider_0.qualifyingUpcomingAppointmentCount ?? 0;
        const locationEhrId = period === "past" ? provider_0.mostRecentPastLocationEhrId : provider_0.earliestUpcomingLocationEhrId;
        const location = locationEhrId ? getTprDepartmentName(locationEhrId) ?? `Athena department ${locationEhrId}` : null;
        return count > 0 ? [`${provider_0.providerName}${provider_0.providerNpi ? ` · ${provider_0.providerNpi}` : ""}${location ? ` · ${location}` : ""} (${count})`] : [];
      });
      if (providers.length > 0) {
        return providers.join(", ");
      }
      if (period === "upcoming" && !hasQualificationEvidence && upcomingAppointmentProviderSummary) {
        return upcomingAppointmentProviderSummary;
      }
      if (!hasQualificationEvidence && (evidence.selectedStep === "usual_provider" || evidence.appointmentProviders.length > 0)) {
        return "Not captured for this legacy task";
      }
      return "None";
    };
    $[2] = evidence.appointmentProviders;
    $[3] = evidence.selectedStep;
    $[4] = hasQualificationEvidence;
    $[5] = upcomingAppointmentProviderSummary;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const providerCountSummary = t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[7] !== diagnosis || $[8] !== evidence.pastAppointmentCount || $[9] !== evidence.selectedStep || $[10] !== evidence.upcomingAppointmentCount || $[11] !== providerCountSummary || $[12] !== referralProviderName || $[13] !== referralProviderNpi || $[14] !== selectedLocation || $[15] !== selectedProviderName) {
    const steps = [{
      key: "upcoming_appointment",
      title: "Check for an upcoming appointment",
      items: [{
        label: "Upcoming within 60 days",
        value: evidence.upcomingAppointmentCount
      }, {
        label: "Qualifying Biltmore providers and locations",
        value: providerCountSummary("upcoming")
      }]
    }, {
      key: "past_appointment",
      title: "Check past appointments at Biltmore locations",
      items: [{
        label: "Completed in past 3 years",
        value: evidence.pastAppointmentCount
      }, {
        label: "Qualifying Biltmore providers and locations",
        value: providerCountSummary("past")
      }]
    }, {
      key: "referral_provider",
      title: "Use the provider named on the referral",
      items: [{
        label: "Provider",
        value: referralProviderName
      }, {
        label: "NPI",
        value: referralProviderNpi
      }]
    }, {
      key: "diagnosis_match",
      title: "Sanity-check the pick or find a replacement",
      items: [{
        label: "Diagnosis",
        value: diagnosis
      }, {
        label: "Location",
        value: selectedLocation
      }, {
        label: "Final provider",
        value: selectedProviderName
      }]
    }];
    t6 = "space-y-2 border-t pt-3";
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <summary className="cursor-pointer text-sm font-medium">Find the Provider and Location</summary>;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    t5 = "mt-2 space-y-2";
    t3 = "space-y-2";
    t4 = steps.map((step, index) => {
      const selected = evidence.selectedStep === step.key;
      return <li key={step.key} className={selected ? "space-y-2 rounded-md border border-success/40 bg-success/10 p-3" : "space-y-2 rounded-md border bg-muted/10 p-3"}><div className="flex items-center justify-between gap-3"><span className="text-sm font-medium">Step {index + 1} — {step.title}</span>{selected ? <Badge variant="outline" className="border-success/40 bg-success/10 text-success">Selected</Badge> : null}</div><DecisionMetadata items={selected ? [...step.items, {
          label: "Picked provider",
          value: selectedProviderName
        }, {
          label: "Reason",
          value: providerSelectionReason(evidence.selectedStep)
        }] : step.items} /></li>;
    });
    $[7] = diagnosis;
    $[8] = evidence.pastAppointmentCount;
    $[9] = evidence.selectedStep;
    $[10] = evidence.upcomingAppointmentCount;
    $[11] = providerCountSummary;
    $[12] = referralProviderName;
    $[13] = referralProviderNpi;
    $[14] = selectedLocation;
    $[15] = selectedProviderName;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
  }
  let t8;
  if ($[22] !== t3 || $[23] !== t4) {
    t8 = <ol className={t3}>{t4}</ol>;
    $[22] = t3;
    $[23] = t4;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== evidence.selectedStep) {
    t9 = evidence.selectedStep === "usual_provider" ? <div className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-warn-foreground">This assignment was created with the retired Athena usual-provider rule. Verify the provider and location against qualifying appointment history before following it.</div> : null;
    $[25] = evidence.selectedStep;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t5 || $[28] !== t8 || $[29] !== t9) {
    t10 = <div className={t5}>{t8}{t9}</div>;
    $[27] = t5;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t6 || $[33] !== t7) {
    t11 = <details className={t6}>{t7}{t10}</details>;
    $[31] = t10;
    $[32] = t6;
    $[33] = t7;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
}
function _temp2(provider) {
  return provider.qualifyingUpcomingAppointmentCount !== undefined || provider.qualifyingPastAppointmentCount !== undefined;
}
const TIMELINE_STATUS = {
  automated: {
    label: "Automated",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />
  },
  verified: {
    label: "Verified",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />
  },
  verify: {
    label: "Verify",
    className: "border-warn/30 bg-warn/10 text-warn-foreground",
    icon: <CircleAlert />
  },
  needs_action: {
    label: "Needs action",
    className: "border-warn-foreground/30 bg-warn-foreground/10 text-warn-foreground",
    icon: <CircleAlert />
  },
  failed: {
    label: "Failed",
    className: "border-destructive-foreground/30 bg-destructive-foreground/10 text-destructive-foreground",
    icon: <CircleAlert />
  },
  waiting: {
    label: "Waiting",
    className: "border-border bg-muted text-muted-foreground",
    icon: <Clock3 />
  },
  not_run: {
    label: "Not run",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />
  },
  not_needed: {
    label: "Not needed",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />
  }
} satisfies Record<TimelineStatus, {
  label: string;
  className: string;
  icon: React.ReactNode;
}>;
function taskStepStatus(taskSteps: TaskStep[], keys: StepKey[]): TimelineStatus {
  const rows = keys.map(key => taskSteps.find(step => step.stepKey === key)).filter(Boolean);
  if (rows.some(row => row?.status === TaskStepStatus.FAILED)) return "failed";
  if (rows.some(row => row?.status === TaskStepStatus.ASYNC || row?.status === TaskStepStatus.PAUSED)) {
    return "waiting";
  }
  if (keys.every(key => taskSteps.some(step => step.stepKey === key && step.status === TaskStepStatus.COMPLETED))) {
    return "automated";
  }
  return "waiting";
}
function TimelineStepCard(t0) {
  const $ = _c(20);
  const {
    task,
    step,
    failedStepKey
  } = t0;
  const effectiveStatus = failedStepKey ? "failed" : step.status;
  const status = TIMELINE_STATUS[effectiveStatus];
  let t1;
  if ($[0] !== failedStepKey || $[1] !== step.continuation || $[2] !== task) {
    t1 = failedStepKey ? {
      instruction: "After completing this step manually, continue with the remaining steps.",
      control: <TprManualStepContinueButton task={task} stepKey={failedStepKey} />
    } : step.continuation;
    $[0] = failedStepKey;
    $[1] = step.continuation;
    $[2] = task;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const continuation = t1;
  const cardVariant = effectiveStatus === "automated" || effectiveStatus === "verified" ? "success" : effectiveStatus === "failed" ? "destructive" : effectiveStatus === "needs_action" || effectiveStatus === "verify" ? "warn" : "outline";
  const t2 = `${step.number}. ${step.title}`;
  const t3 = effectiveStatus === "automated" ? undefined : status.icon;
  const t4 = effectiveStatus === "not_run" ? "bg-muted/20 text-muted-foreground opacity-40 grayscale" : undefined;
  let t5;
  if ($[4] !== effectiveStatus || $[5] !== status) {
    t5 = effectiveStatus === "automated" ? <span className="text-success-foreground" aria-label="Automated"><CheckCircleIcon weight="fill" className="size-5" /></span> : <Badge variant="outline" className={status.className}>{status.label}</Badge>;
    $[4] = effectiveStatus;
    $[5] = status;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== step.summary) {
    t6 = <p className="text-sm text-muted-foreground">{step.summary}</p>;
    $[7] = step.summary;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== continuation) {
    t7 = continuation ? <div className="space-y-3 border-t pt-4"><p className="text-sm text-muted-foreground">{continuation.instruction}</p><div className="flex justify-center">{continuation.control}</div></div> : null;
    $[9] = continuation;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== cardVariant || $[12] !== step.content || $[13] !== t2 || $[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = <ValerieCard title={t2} icon={t3} className={t4} variant={cardVariant} actions={t5} contentClassName="space-y-3">{t6}{step.content}{t7}</ValerieCard>;
    $[11] = cardVariant;
    $[12] = step.content;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
function TprStediEligibilityValues(t0) {
  const $ = _c(5);
  const {
    referralId
  } = t0;
  const {
    eligibilityCheck
  } = useStediEligibilityCheck(referralId);
  let t1;
  if ($[0] !== eligibilityCheck) {
    t1 = <StediEligibilitySummary check={eligibilityCheck} showHeader={false} />;
    $[0] = eligibilityCheck;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="text-sm text-muted-foreground">Recheck or edit coverage on the Information tab.</p>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <div className="space-y-3">{t1}{t2}</div>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function TprSyncTimeline(t0) {
  const $ = _c(316);
  const {
    task,
    referralId,
    taskSteps,
    pendingApproval,
    receivingAssignmentConfirmed,
    followThroughEligible,
    coverageReadback,
    insuranceRequirements,
    referralAuthorization,
    resolvedReceiving,
    patientLabel,
    patientEhrId,
    documentId,
    bucketLabel,
    actionNote,
    insuranceStatusDisplay,
    hasMissingInformation,
    unresolvedUpcomingProviders,
    upcomingAppointmentProviderSummary,
    upcomingAppointments,
    pastAppointments,
    documentUploadLabels,
    privacyNoticeOnFile,
    isPrimaryCareProvider,
    primaryCareProviderAlreadyPresent,
    patientDisplayId,
    opsCrossCheck
  } = t0;
  let t1;
  if ($[0] !== taskSteps) {
    t1 = taskSteps.some(_temp3);
    $[0] = taskSteps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isProviderFirst = t1;
  let t2;
  if ($[2] !== pastAppointments || $[3] !== resolvedReceiving.providerSelectionEvidence || $[4] !== upcomingAppointments) {
    t2 = resolvedReceiving.providerSelectionEvidence ?? {
      selectedStep: null,
      upcomingAppointmentCount: upcomingAppointments.length,
      pastAppointmentCount: pastAppointments.length,
      appointmentProviders: []
    };
    $[2] = pastAppointments;
    $[3] = resolvedReceiving.providerSelectionEvidence;
    $[4] = upcomingAppointments;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const providerSelectionEvidence = t2;
  const workflowStillProcessing = task.status === TaskStatus.PROCESSING;
  let coverageApproval;
  let failedStepCard;
  let opsApproval;
  let steps;
  let t3;
  let waitingForAssociatedReferral;
  if ($[6] !== actionNote || $[7] !== bucketLabel || $[8] !== coverageReadback || $[9] !== documentId || $[10] !== documentUploadLabels || $[11] !== followThroughEligible || $[12] !== hasMissingInformation || $[13] !== insuranceRequirements || $[14] !== insuranceStatusDisplay || $[15] !== isPrimaryCareProvider || $[16] !== isProviderFirst || $[17] !== opsCrossCheck.diagnosis || $[18] !== opsCrossCheck.emergencyContactName || $[19] !== opsCrossCheck.emergencyContactPhoneNumber || $[20] !== opsCrossCheck.emergencyContactRelationship || $[21] !== opsCrossCheck.patientDateOfBirth || $[22] !== opsCrossCheck.patientHomeAddress || $[23] !== opsCrossCheck.patientName || $[24] !== opsCrossCheck.patientPhoneNumber || $[25] !== opsCrossCheck.primaryPlanLabel || $[26] !== opsCrossCheck.primarySubscriberId || $[27] !== opsCrossCheck.receivingProviderName || $[28] !== opsCrossCheck.receivingProviderNpi || $[29] !== opsCrossCheck.referralDate || $[30] !== opsCrossCheck.referringLocationAddress || $[31] !== opsCrossCheck.referringLocationName || $[32] !== opsCrossCheck.referringProviderName || $[33] !== opsCrossCheck.referringProviderNpi || $[34] !== opsCrossCheck.referringProviderSpecialty || $[35] !== patientDisplayId || $[36] !== patientEhrId || $[37] !== patientLabel || $[38] !== pendingApproval || $[39] !== primaryCareProviderAlreadyPresent || $[40] !== privacyNoticeOnFile || $[41] !== providerSelectionEvidence || $[42] !== receivingAssignmentConfirmed || $[43] !== referralAuthorization || $[44] !== referralId || $[45] !== resolvedReceiving.flags || $[46] !== resolvedReceiving.locationLabel || $[47] !== resolvedReceiving.providerLabel || $[48] !== resolvedReceiving.providerNpi || $[49] !== resolvedReceiving.providerWriteSafe || $[50] !== resolvedReceiving.usualProviderWriteSafe || $[51] !== task || $[52] !== taskSteps || $[53] !== unresolvedUpcomingProviders || $[54] !== upcomingAppointmentProviderSummary || $[55] !== workflowStillProcessing) {
    const assignmentStatus = isProviderFirst ? taskStepStatus(taskSteps, [StepKey.RESOLVE_RECEIVING_ASSIGNMENT]) : workflowStillProcessing ? "waiting" : "not_run";
    const assignmentDependentStatus = assignmentStatus === "failed" ? "not_run" : assignmentStatus;
    const receivingAssignmentApproval = pendingApproval?.approvalType === ApprovalType.TPR_RECEIVING_ASSIGNMENT ? pendingApproval : null;
    const assignmentRequiresManualReview = assignmentStatus === "automated" && !resolvedReceiving.providerWriteSafe && receivingAssignmentApproval !== null;
    const usesRetiredUsualProviderRule = providerSelectionEvidence.selectedStep === "usual_provider";
    const assignmentNeedsManualConfirmation = !resolvedReceiving.providerWriteSafe || usesRetiredUsualProviderRule;
    const assignmentWasManuallyConfirmed = assignmentStatus === "automated" && assignmentNeedsManualConfirmation && receivingAssignmentConfirmed;
    const assignmentReviewStatus = assignmentWasManuallyConfirmed ? "verified" : assignmentRequiresManualReview || usesRetiredUsualProviderRule ? "needs_action" : assignmentDependentStatus;
    const gateStatus = taskStepStatus(taskSteps, [StepKey.TPR_CAMELBACK_FOLLOW_THROUGH]);
    const assignmentConfirmationStatus = assignmentWasManuallyConfirmed ? "verified" : assignmentReviewStatus === "needs_action" ? "needs_action" : assignmentStatus === "automated" ? gateStatus : assignmentStatus;
    const confirmedNonCamelbackAssignment = assignmentStatus === "automated" && !followThroughEligible && receivingAssignmentApproval === null;
    const skippedFollowThroughStatus = confirmedNonCamelbackAssignment ? "not_needed" : "not_run";
    const skippedFollowThroughSummary = assignmentRequiresManualReview ? "Not run because the receiving provider and location require manual confirmation." : confirmedNonCamelbackAssignment ? "Not needed for the resolved non-Camelback receiving assignment." : "Not run because the receiving assignment has not completed.";
    coverageApproval = pendingApproval?.approvalType === ApprovalType.ATHENA_COVERAGE ? pendingApproval : null;
    opsApproval = pendingApproval?.approvalType === ApprovalType.TPR_REFERRAL_OPS ? pendingApproval : null;
    const opsKind = opsApproval?.metadata?.kind ?? null;
    let t4;
    if ($[62] !== taskSteps) {
      t4 = taskSteps.some(_temp4);
      $[62] = taskSteps;
      $[63] = t4;
    } else {
      t4 = $[63];
    }
    waitingForAssociatedReferral = t4;
    const coverageReadbackComplete = taskSteps.some(_temp5);
    let t5;
    if ($[64] !== taskSteps) {
      t5 = taskSteps.some(_temp6);
      $[64] = taskSteps;
      $[65] = t5;
    } else {
      t5 = $[65];
    }
    const authRequestUploaded = t5;
    let t6;
    if ($[66] !== taskSteps) {
      t6 = taskSteps.some(_temp7);
      $[66] = taskSteps;
      $[67] = t6;
    } else {
      t6 = $[67];
    }
    const notesComplete = t6;
    const missingInfoUploadComplete = taskSteps.some(_temp8);
    const verificationActionStepNumber = task.status === TaskStatus.VERIFICATION_NEEDED ? 14 : null;
    let t7;
    if ($[68] !== followThroughEligible || $[69] !== skippedFollowThroughStatus || $[70] !== taskSteps) {
      t7 = keys => followThroughEligible ? taskStepStatus(taskSteps, keys) : skippedFollowThroughStatus;
      $[68] = followThroughEligible;
      $[69] = skippedFollowThroughStatus;
      $[70] = taskSteps;
      $[71] = t7;
    } else {
      t7 = $[71];
    }
    const followThroughStatus = t7;
    let coverageStatus = assignmentRequiresManualReview ? "not_run" : taskStepStatus(taskSteps, [StepKey.CHECK_INSURANCE_ELIGIBILITY, StepKey.CREATE_COVERAGE, StepKey.CONFIRM_ATHENA_COVERAGE, StepKey.READ_ATHENA_COVERAGE]);
    if (coverageApproval) {
      coverageStatus = coverageApproval.metadata?.reviewMode === "verify" ? "verify" : "needs_action";
    } else {
      if (coverageReadbackComplete) {
        coverageStatus = "verified";
      }
    }
    const persistedAthenaEligibilityStatus = followThroughStatus([StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY]);
    const athenaEligibilityStatus = coverageReadback?.primary.selfPay ? "not_needed" : persistedAthenaEligibilityStatus === "automated" && insuranceStatusDisplay === "Unverified" ? "needs_action" : persistedAthenaEligibilityStatus;
    const networkStatus = coverageReadback?.primary.selfPay || referralAuthorization?.outcome === "not_needed" && referralAuthorization.reason === "ineligible" ? "not_needed" : insuranceRequirements && (insuranceRequirements.clinicNetwork === "manual_review" || insuranceRequirements.providerNetwork === "manual_review" || insuranceRequirements.additionalSchedulingInformationRequired) ? "needs_action" : followThroughStatus([StepKey.RESOLVE_TPR_INSURANCE_REQUIREMENTS]);
    const authorizationStatus = opsKind === "authorization_review" ? "verify" : opsKind === "manual_authorization" ? "needs_action" : tprReferralAuthNeedsReview(referralAuthorization) ? "needs_action" : referralAuthorization?.outcome === "not_needed" ? "not_needed" : referralAuthorization?.outcome === "needs_pending_request" ? "automated" : followThroughStatus([StepKey.TPR_REFERRAL_AUTHORIZATION]);
    let t8;
    if ($[72] !== referralAuthorization) {
      t8 = tprReferralAuthNeedsPendingUpload(referralAuthorization);
      $[72] = referralAuthorization;
      $[73] = t8;
    } else {
      t8 = $[73];
    }
    const referralAuthNeedsPendingUpload = t8;
    const pcpFaxStatus = opsKind === "pcp_referral_fax" ? "needs_action" : insuranceRequirements && !insuranceRequirements.referralMustBeFromPcp ? "not_needed" : followThroughStatus([StepKey.TPR_PCP_REFERRAL_FAX]);
    const authRequestUploadStatus = opsKind === "auth_request_upload" ? "needs_action" : referralAuthNeedsPendingUpload ? authRequestUploaded ? "automated" : followThroughStatus([StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD]) : "not_needed";
    const closeDocumentStatus = opsKind === "close_document" ? "needs_action" : followThroughStatus([StepKey.TPR_CLOSE_DOCUMENT]);
    let fullRegistrationReviewReasons;
    let t9;
    if ($[74] !== privacyNoticeOnFile || $[75] !== task.files) {
      const workflowNote = task.files.find(_temp9)?.note;
      const t10 = privacyNoticeOnFile === false || workflowNote?.includes("Athena privacy notice could not be set automatically") ? "Set Athena Privacy to YES manually." : null;
      let t11;
      if ($[78] !== t10) {
        t11 = [t10].filter(_temp10);
        $[78] = t10;
        $[79] = t11;
      } else {
        t11 = $[79];
      }
      fullRegistrationReviewReasons = t11;
      t9 = workflowNote?.includes("could not be matched in Athena and was not added to the patient's care team") ? "Add the unmatched referring provider to the Athena care team manually." : null;
      $[74] = privacyNoticeOnFile;
      $[75] = task.files;
      $[76] = fullRegistrationReviewReasons;
      $[77] = t9;
    } else {
      fullRegistrationReviewReasons = $[76];
      t9 = $[77];
    }
    let t10;
    if ($[80] !== t9) {
      t10 = [t9].filter(_temp11);
      $[80] = t9;
      $[81] = t10;
    } else {
      t10 = $[81];
    }
    const demographicsReviewReasons = t10;
    const ensurePatientStatus = taskStepStatus(taskSteps, [StepKey.ENSURE_PATIENT]);
    const careTeamStatus = taskStepStatus(taskSteps, [StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM]);
    const fullRegistrationStatus = ensurePatientStatus === "failed" ? "not_run" : ensurePatientStatus === "waiting" ? "waiting" : fullRegistrationReviewReasons.length > 0 ? "needs_action" : "verify";
    const demographicsStatus = ensurePatientStatus === "failed" ? "not_run" : careTeamStatus !== "automated" ? careTeamStatus : demographicsReviewReasons.length > 0 ? "needs_action" : "automated";
    const dispositionStatus = taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION]);
    const unevaluatedInsuranceDecision = followThroughEligible ? "Pending" : "Not evaluated";
    const inNetworkDisplay = !insuranceRequirements ? unevaluatedInsuranceDecision : insuranceRequirements.clinicNetwork === "out_of_network" || insuranceRequirements.providerNetwork === "out_of_network" ? "No" : insuranceRequirements.clinicNetwork === "in_network" && insuranceRequirements.providerNetwork === "in_network" ? "Yes" : insuranceRequirements.clinicNetwork === "not_applicable" && insuranceRequirements.providerNetwork === "not_applicable" ? "Not applicable" : "Manual review";
    let t11;
    if ($[82] !== insuranceRequirements || $[83] !== unevaluatedInsuranceDecision) {
      t11 = insuranceRequirements ? formatFromUnderscoreToHumanReadable(insuranceRequirements.referralRequirement) : unevaluatedInsuranceDecision;
      $[82] = insuranceRequirements;
      $[83] = unevaluatedInsuranceDecision;
      $[84] = t11;
    } else {
      t11 = $[84];
    }
    const referralRequirementDisplay = t11;
    let t12;
    if ($[85] !== insuranceRequirements || $[86] !== unevaluatedInsuranceDecision) {
      t12 = insuranceRequirements ? formatFromUnderscoreToHumanReadable(insuranceRequirements.authorizationRequirement) : unevaluatedInsuranceDecision;
      $[85] = insuranceRequirements;
      $[86] = unevaluatedInsuranceDecision;
      $[87] = t12;
    } else {
      t12 = $[87];
    }
    const authorizationRequirementDisplay = t12;
    let t13;
    if ($[88] !== opsCrossCheck.primaryPlanLabel) {
      t13 = {
        label: "Valerie plan",
        value: opsCrossCheck.primaryPlanLabel
      };
      $[88] = opsCrossCheck.primaryPlanLabel;
      $[89] = t13;
    } else {
      t13 = $[89];
    }
    let t14;
    if ($[90] !== opsCrossCheck.primarySubscriberId) {
      t14 = {
        label: "Valerie subscriber ID",
        value: opsCrossCheck.primarySubscriberId
      };
      $[90] = opsCrossCheck.primarySubscriberId;
      $[91] = t14;
    } else {
      t14 = $[91];
    }
    let t15;
    if ($[92] !== inNetworkDisplay) {
      t15 = {
        label: "In network",
        value: inNetworkDisplay
      };
      $[92] = inNetworkDisplay;
      $[93] = t15;
    } else {
      t15 = $[93];
    }
    let t16;
    if ($[94] !== referralRequirementDisplay) {
      t16 = {
        label: "Referral",
        value: referralRequirementDisplay
      };
      $[94] = referralRequirementDisplay;
      $[95] = t16;
    } else {
      t16 = $[95];
    }
    let t17;
    if ($[96] !== authorizationRequirementDisplay) {
      t17 = {
        label: "Authorization",
        value: authorizationRequirementDisplay
      };
      $[96] = authorizationRequirementDisplay;
      $[97] = t17;
    } else {
      t17 = $[97];
    }
    let t18;
    if ($[98] !== t13 || $[99] !== t14 || $[100] !== t15 || $[101] !== t16 || $[102] !== t17) {
      t18 = [t13, t14, t15, t16, t17];
      $[98] = t13;
      $[99] = t14;
      $[100] = t15;
      $[101] = t16;
      $[102] = t17;
      $[103] = t18;
    } else {
      t18 = $[103];
    }
    const insuranceCrossCheckItems = t18;
    const insuranceIntakeDetails = coverageApproval?.metadata ? <div className="space-y-3 rounded-md border p-3 text-sm"><DecisionMetadata items={[...insuranceCrossCheckItems, {
        label: "Expected",
        value: coverageApproval.metadata.expectedCoverageCount
      }, {
        label: "In Athena",
        value: coverageApproval.metadata.currentCoverages.length
      }]} /><AthenaCoverageEvidence metadata={coverageApproval.metadata} /></div> : coverageReadback ? <div className="space-y-3 text-sm"><DecisionMetadata items={[...insuranceCrossCheckItems, {
        label: "In Athena",
        value: coverageReadback.coverages.length
      }, {
        label: "Self pay",
        value: coverageReadback.primary.selfPay ? "Yes" : "No"
      }]} /><div className="space-y-1"><p className="font-medium">Currently in Athena</p>{coverageReadback.coverages.length === 0 ? <p>No coverage found.</p> : coverageReadback.coverages.map(_temp12)}</div></div> : followThroughEligible || assignmentStatus === "automated" ? <DecisionMetadata items={insuranceCrossCheckItems} /> : null;
    const t19 = `${opsCrossCheck.patientName} (DOB: ${opsCrossCheck.patientDateOfBirth})`;
    let t20;
    if ($[104] !== t19) {
      t20 = {
        label: "Patient",
        value: <CopyableContent text={t19} />
      };
      $[104] = t19;
      $[105] = t20;
    } else {
      t20 = $[105];
    }
    let t21;
    if ($[106] !== patientEhrId) {
      t21 = {
        label: "EHR ID",
        value: <CopyableContent text={patientEhrId} />
      };
      $[106] = patientEhrId;
      $[107] = t21;
    } else {
      t21 = $[107];
    }
    let t22;
    if ($[108] !== documentId) {
      t22 = {
        label: "Document ID",
        value: <CopyableContent text={documentId} />
      };
      $[108] = documentId;
      $[109] = t22;
    } else {
      t22 = $[109];
    }
    let t23;
    if ($[110] !== t20 || $[111] !== t21 || $[112] !== t22) {
      t23 = <DecisionMetadata items={[t20, t21, t22]} />;
      $[110] = t20;
      $[111] = t21;
      $[112] = t22;
      $[113] = t23;
    } else {
      t23 = $[113];
    }
    let t24;
    if ($[114] !== patientDisplayId) {
      t24 = patientDisplayId ? <Link href={`/patients/${patientDisplayId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground">Open patient details<SquareArrowOutUpRight className="size-3.5" /></Link> : null;
      $[114] = patientDisplayId;
      $[115] = t24;
    } else {
      t24 = $[115];
    }
    let t25;
    if ($[116] !== t23 || $[117] !== t24) {
      t25 = <div className="space-y-3">{t23}{t24}</div>;
      $[116] = t23;
      $[117] = t24;
      $[118] = t25;
    } else {
      t25 = $[118];
    }
    let t26;
    if ($[119] !== patientLabel) {
      t26 = {
        label: "Patient type",
        value: patientLabel
      };
      $[119] = patientLabel;
      $[120] = t26;
    } else {
      t26 = $[120];
    }
    const t27 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
    let t28;
    if ($[121] !== t27) {
      t28 = {
        label: "Provider",
        value: t27
      };
      $[121] = t27;
      $[122] = t28;
    } else {
      t28 = $[122];
    }
    const t29 = resolvedReceiving.providerNpi || NOT_AVAILABLE;
    let t30;
    if ($[123] !== t29) {
      t30 = {
        label: "NPI",
        value: t29
      };
      $[123] = t29;
      $[124] = t30;
    } else {
      t30 = $[124];
    }
    const t31 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
    let t32;
    if ($[125] !== t31) {
      t32 = {
        label: "Location",
        value: t31
      };
      $[125] = t31;
      $[126] = t32;
    } else {
      t32 = $[126];
    }
    const t33 = usesRetiredUsualProviderRule ? "Verify appointment history" : assignmentRequiresManualReview ? "Manual review" : "Confirmed";
    let t34;
    if ($[127] !== t33) {
      t34 = {
        label: "Resolution",
        value: t33
      };
      $[127] = t33;
      $[128] = t34;
    } else {
      t34 = $[128];
    }
    let t35;
    if ($[129] !== t26 || $[130] !== t28 || $[131] !== t30 || $[132] !== t32 || $[133] !== t34) {
      t35 = <DecisionMetadata items={[t26, t28, t30, t32, t34]} />;
      $[129] = t26;
      $[130] = t28;
      $[131] = t30;
      $[132] = t32;
      $[133] = t34;
      $[134] = t35;
    } else {
      t35 = $[134];
    }
    const t36 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
    const t37 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
    let t38;
    if ($[135] !== opsCrossCheck.diagnosis || $[136] !== opsCrossCheck.receivingProviderName || $[137] !== opsCrossCheck.receivingProviderNpi || $[138] !== providerSelectionEvidence || $[139] !== t36 || $[140] !== t37 || $[141] !== upcomingAppointmentProviderSummary) {
      t38 = <ProviderSelectionChecklist evidence={providerSelectionEvidence} upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary} referralProviderName={opsCrossCheck.receivingProviderName} referralProviderNpi={opsCrossCheck.receivingProviderNpi} diagnosis={opsCrossCheck.diagnosis} selectedProviderName={t36} selectedLocation={t37} />;
      $[135] = opsCrossCheck.diagnosis;
      $[136] = opsCrossCheck.receivingProviderName;
      $[137] = opsCrossCheck.receivingProviderNpi;
      $[138] = providerSelectionEvidence;
      $[139] = t36;
      $[140] = t37;
      $[141] = upcomingAppointmentProviderSummary;
      $[142] = t38;
    } else {
      t38 = $[142];
    }
    const t39 = assignmentConfirmationStatus === "needs_action" && resolvedReceiving.flags.length > 0 ? <><ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{resolvedReceiving.flags.map(_temp13)}</ul>{unresolvedUpcomingProviders.length > 0 ? <div className="flex flex-wrap items-center gap-2 rounded-md border border-warn/30 bg-warn/5 p-3"><span className="text-xs font-medium text-muted-foreground">Upcoming appointment provider</span>{unresolvedUpcomingProviders.map(_temp14)}</div> : null}</> : null;
    let t40;
    if ($[143] !== t35 || $[144] !== t38 || $[145] !== t39) {
      t40 = <div className="space-y-3">{t35}{t38}{t39}</div>;
      $[143] = t35;
      $[144] = t38;
      $[145] = t39;
      $[146] = t40;
    } else {
      t40 = $[146];
    }
    let t41;
    if ($[147] === Symbol.for("react.memo_cache_sentinel")) {
      t41 = <p className="text-sm font-medium">Patient Details</p>;
      $[147] = t41;
    } else {
      t41 = $[147];
    }
    let t42;
    if ($[148] !== opsCrossCheck.patientName) {
      t42 = {
        label: "Patient",
        value: opsCrossCheck.patientName
      };
      $[148] = opsCrossCheck.patientName;
      $[149] = t42;
    } else {
      t42 = $[149];
    }
    let t43;
    if ($[150] !== opsCrossCheck.patientDateOfBirth) {
      t43 = {
        label: "DOB",
        value: opsCrossCheck.patientDateOfBirth
      };
      $[150] = opsCrossCheck.patientDateOfBirth;
      $[151] = t43;
    } else {
      t43 = $[151];
    }
    let t44;
    if ($[152] !== opsCrossCheck.patientPhoneNumber) {
      t44 = {
        label: "Phone",
        value: opsCrossCheck.patientPhoneNumber
      };
      $[152] = opsCrossCheck.patientPhoneNumber;
      $[153] = t44;
    } else {
      t44 = $[153];
    }
    let t45;
    if ($[154] !== opsCrossCheck.patientHomeAddress) {
      t45 = {
        label: "Home address",
        value: opsCrossCheck.patientHomeAddress
      };
      $[154] = opsCrossCheck.patientHomeAddress;
      $[155] = t45;
    } else {
      t45 = $[155];
    }
    let t46;
    if ($[156] !== t42 || $[157] !== t43 || $[158] !== t44 || $[159] !== t45) {
      t46 = <div className="space-y-2 rounded-md border p-3">{t41}<DecisionMetadata items={[t42, t43, t44, t45]} /></div>;
      $[156] = t42;
      $[157] = t43;
      $[158] = t44;
      $[159] = t45;
      $[160] = t46;
    } else {
      t46 = $[160];
    }
    let t47;
    if ($[161] === Symbol.for("react.memo_cache_sentinel")) {
      t47 = <p className="text-sm font-medium">Care Team</p>;
      $[161] = t47;
    } else {
      t47 = $[161];
    }
    let t48;
    if ($[162] === Symbol.for("react.memo_cache_sentinel")) {
      t48 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Usual Provider</p>;
      $[162] = t48;
    } else {
      t48 = $[162];
    }
    let t49;
    if ($[163] !== resolvedReceiving.providerLabel || $[164] !== resolvedReceiving.providerNpi || $[165] !== resolvedReceiving.usualProviderWriteSafe) {
      t49 = <div className="space-y-1">{t48}<DecisionMetadata items={resolvedReceiving.usualProviderWriteSafe ? [{
          label: "Provider",
          value: resolvedReceiving.providerLabel || NOT_AVAILABLE
        }, {
          label: "NPI",
          value: resolvedReceiving.providerNpi || NOT_AVAILABLE
        }, {
          label: "Action",
          value: "Update (new patient)"
        }] : [{
          label: "Action",
          value: "Leave unchanged (established patient)"
        }]} /></div>;
      $[163] = resolvedReceiving.providerLabel;
      $[164] = resolvedReceiving.providerNpi;
      $[165] = resolvedReceiving.usualProviderWriteSafe;
      $[166] = t49;
    } else {
      t49 = $[166];
    }
    let t50;
    if ($[167] === Symbol.for("react.memo_cache_sentinel")) {
      t50 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Referring Provider</p>;
      $[167] = t50;
    } else {
      t50 = $[167];
    }
    let t51;
    if ($[168] !== opsCrossCheck.referringProviderName) {
      t51 = {
        label: "Name",
        value: <CopyableContent text={opsCrossCheck.referringProviderName} />
      };
      $[168] = opsCrossCheck.referringProviderName;
      $[169] = t51;
    } else {
      t51 = $[169];
    }
    let t52;
    if ($[170] !== opsCrossCheck.referringProviderNpi) {
      t52 = {
        label: "NPI",
        value: <CopyableContent text={opsCrossCheck.referringProviderNpi} />
      };
      $[170] = opsCrossCheck.referringProviderNpi;
      $[171] = t52;
    } else {
      t52 = $[171];
    }
    let t53;
    if ($[172] !== opsCrossCheck.referringLocationAddress) {
      t53 = {
        label: "Address",
        value: <CopyableContent text={opsCrossCheck.referringLocationAddress} />
      };
      $[172] = opsCrossCheck.referringLocationAddress;
      $[173] = t53;
    } else {
      t53 = $[173];
    }
    let t54;
    if ($[174] !== opsCrossCheck.referringProviderSpecialty) {
      t54 = {
        label: "Specialty",
        value: opsCrossCheck.referringProviderSpecialty
      };
      $[174] = opsCrossCheck.referringProviderSpecialty;
      $[175] = t54;
    } else {
      t54 = $[175];
    }
    let t55;
    if ($[176] !== t51 || $[177] !== t52 || $[178] !== t53 || $[179] !== t54) {
      t55 = <div className="space-y-1">{t50}<DecisionMetadata items={[t51, t52, t53, t54]} /></div>;
      $[176] = t51;
      $[177] = t52;
      $[178] = t53;
      $[179] = t54;
      $[180] = t55;
    } else {
      t55 = $[180];
    }
    let t56;
    if ($[181] !== isPrimaryCareProvider || $[182] !== opsCrossCheck.referringProviderName || $[183] !== opsCrossCheck.referringProviderNpi || $[184] !== primaryCareProviderAlreadyPresent) {
      t56 = isPrimaryCareProvider && !primaryCareProviderAlreadyPresent ? <div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">PCP</p><DecisionMetadata items={[{
          label: "Name",
          value: <CopyableContent text={opsCrossCheck.referringProviderName} />
        }, {
          label: "NPI",
          value: <CopyableContent text={opsCrossCheck.referringProviderNpi} />
        }]} /></div> : null;
      $[181] = isPrimaryCareProvider;
      $[182] = opsCrossCheck.referringProviderName;
      $[183] = opsCrossCheck.referringProviderNpi;
      $[184] = primaryCareProviderAlreadyPresent;
      $[185] = t56;
    } else {
      t56 = $[185];
    }
    let t57;
    if ($[186] !== t49 || $[187] !== t55 || $[188] !== t56) {
      t57 = <div className="space-y-3 rounded-md border p-3">{t47}{t49}{t55}{t56}</div>;
      $[186] = t49;
      $[187] = t55;
      $[188] = t56;
      $[189] = t57;
    } else {
      t57 = $[189];
    }
    let t58;
    if ($[190] === Symbol.for("react.memo_cache_sentinel")) {
      t58 = <p className="text-sm font-medium">Emergency Contact</p>;
      $[190] = t58;
    } else {
      t58 = $[190];
    }
    let t59;
    if ($[191] !== opsCrossCheck.emergencyContactName) {
      t59 = {
        label: "Name",
        value: opsCrossCheck.emergencyContactName
      };
      $[191] = opsCrossCheck.emergencyContactName;
      $[192] = t59;
    } else {
      t59 = $[192];
    }
    let t60;
    if ($[193] !== opsCrossCheck.emergencyContactRelationship) {
      t60 = {
        label: "Relationship",
        value: opsCrossCheck.emergencyContactRelationship
      };
      $[193] = opsCrossCheck.emergencyContactRelationship;
      $[194] = t60;
    } else {
      t60 = $[194];
    }
    let t61;
    if ($[195] !== opsCrossCheck.emergencyContactPhoneNumber) {
      t61 = {
        label: "Phone",
        value: opsCrossCheck.emergencyContactPhoneNumber
      };
      $[195] = opsCrossCheck.emergencyContactPhoneNumber;
      $[196] = t61;
    } else {
      t61 = $[196];
    }
    let t62;
    if ($[197] !== t59 || $[198] !== t60 || $[199] !== t61) {
      t62 = <div className="space-y-2 rounded-md border p-3">{t58}<DecisionMetadata items={[t59, t60, t61]} /></div>;
      $[197] = t59;
      $[198] = t60;
      $[199] = t61;
      $[200] = t62;
    } else {
      t62 = $[200];
    }
    let t63;
    if ($[201] !== demographicsReviewReasons) {
      t63 = demographicsReviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{demographicsReviewReasons.map(_temp15)}</ul> : null;
      $[201] = demographicsReviewReasons;
      $[202] = t63;
    } else {
      t63 = $[202];
    }
    let t64;
    if ($[203] !== t46 || $[204] !== t57 || $[205] !== t62 || $[206] !== t63) {
      t64 = <div className="space-y-3">{t46}{t57}{t62}{t63}</div>;
      $[203] = t46;
      $[204] = t57;
      $[205] = t62;
      $[206] = t63;
      $[207] = t64;
    } else {
      t64 = $[207];
    }
    let t65;
    if ($[208] !== referralId) {
      t65 = <TprStediEligibilityValues referralId={referralId} />;
      $[208] = referralId;
      $[209] = t65;
    } else {
      t65 = $[209];
    }
    let t66;
    if ($[210] !== insuranceIntakeDetails || $[211] !== t65) {
      t66 = <div className="space-y-3">{insuranceIntakeDetails}{t65}</div>;
      $[210] = insuranceIntakeDetails;
      $[211] = t65;
      $[212] = t66;
    } else {
      t66 = $[212];
    }
    let t67;
    let t68;
    if ($[213] === Symbol.for("react.memo_cache_sentinel")) {
      t67 = {
        label: "Calls",
        value: "Verify Yes"
      };
      t68 = {
        label: "Texts",
        value: "Verify Yes"
      };
      $[213] = t67;
      $[214] = t68;
    } else {
      t67 = $[213];
      t68 = $[214];
    }
    const t69 = privacyNoticeOnFile === null ? "Verify selected" : privacyNoticeOnFile ? "Selected" : "Not selected";
    let t70;
    if ($[215] !== t69) {
      t70 = <DecisionMetadata items={[t67, t68, {
        label: "Privacy Notice",
        value: t69
      }]} />;
      $[215] = t69;
      $[216] = t70;
    } else {
      t70 = $[216];
    }
    let t71;
    if ($[217] === Symbol.for("react.memo_cache_sentinel")) {
      t71 = <ul className="list-disc space-y-1 pl-5 text-sm text-foreground"><li>Verify Calls is Yes.</li><li>Verify Texts is Yes.</li><li>Verify Privacy Notice is selected.</li><li>If “How did you hear about us?” is blank, set it to Physician Referral. Leave any existing value unchanged.</li></ul>;
      $[217] = t71;
    } else {
      t71 = $[217];
    }
    let t72;
    if ($[218] !== fullRegistrationReviewReasons) {
      t72 = fullRegistrationReviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-warn-foreground">{fullRegistrationReviewReasons.map(_temp16)}</ul> : null;
      $[218] = fullRegistrationReviewReasons;
      $[219] = t72;
    } else {
      t72 = $[219];
    }
    let t73;
    if ($[220] !== t70 || $[221] !== t72) {
      t73 = <div className="space-y-3">{t70}{t71}{t72}</div>;
      $[220] = t70;
      $[221] = t72;
      $[222] = t73;
    } else {
      t73 = $[222];
    }
    let t74;
    if ($[223] !== opsCrossCheck.referringProviderName) {
      t74 = {
        label: "Referring provider",
        value: <CopyableContent text={opsCrossCheck.referringProviderName} />
      };
      $[223] = opsCrossCheck.referringProviderName;
      $[224] = t74;
    } else {
      t74 = $[224];
    }
    let t75;
    if ($[225] !== opsCrossCheck.referringProviderNpi) {
      t75 = {
        label: "Referring NPI",
        value: <CopyableContent text={opsCrossCheck.referringProviderNpi} />
      };
      $[225] = opsCrossCheck.referringProviderNpi;
      $[226] = t75;
    } else {
      t75 = $[226];
    }
    let t76;
    if ($[227] !== opsCrossCheck.referringLocationName) {
      t76 = {
        label: "Referring practice/location",
        value: opsCrossCheck.referringLocationName
      };
      $[227] = opsCrossCheck.referringLocationName;
      $[228] = t76;
    } else {
      t76 = $[228];
    }
    let t77;
    if ($[229] !== opsCrossCheck.referringLocationAddress) {
      t77 = {
        label: "Referring address",
        value: opsCrossCheck.referringLocationAddress
      };
      $[229] = opsCrossCheck.referringLocationAddress;
      $[230] = t77;
    } else {
      t77 = $[230];
    }
    let t78;
    if ($[231] !== opsCrossCheck.referralDate) {
      t78 = {
        label: "Referral date",
        value: opsCrossCheck.referralDate
      };
      $[231] = opsCrossCheck.referralDate;
      $[232] = t78;
    } else {
      t78 = $[232];
    }
    let t79;
    if ($[233] !== patientLabel) {
      t79 = {
        label: "Patient type",
        value: patientLabel
      };
      $[233] = patientLabel;
      $[234] = t79;
    } else {
      t79 = $[234];
    }
    let t80;
    if ($[235] !== documentUploadLabels.length) {
      t80 = {
        label: "Documents",
        value: documentUploadLabels.length
      };
      $[235] = documentUploadLabels.length;
      $[236] = t80;
    } else {
      t80 = $[236];
    }
    let t81;
    if ($[237] !== t74 || $[238] !== t75 || $[239] !== t76 || $[240] !== t77 || $[241] !== t78 || $[242] !== t79 || $[243] !== t80) {
      t81 = <DecisionMetadata items={[t74, t75, t76, t77, t78, t79, t80]} />;
      $[237] = t74;
      $[238] = t75;
      $[239] = t76;
      $[240] = t77;
      $[241] = t78;
      $[242] = t79;
      $[243] = t80;
      $[244] = t81;
    } else {
      t81 = $[244];
    }
    let t82;
    if ($[245] === Symbol.for("react.memo_cache_sentinel")) {
      t82 = <p className="text-xs text-muted-foreground">Referring practice/location is the office name; referring address is the physical address used to verify the care-team entry.</p>;
      $[245] = t82;
    } else {
      t82 = $[245];
    }
    let t83;
    if ($[246] === Symbol.for("react.memo_cache_sentinel")) {
      t83 = <p className="text-sm font-medium">Expected Athena uploads</p>;
      $[246] = t83;
    } else {
      t83 = $[246];
    }
    let t84;
    if ($[247] !== documentUploadLabels) {
      t84 = <div className="space-y-1">{t83}{documentUploadLabels.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{documentUploadLabels.map(_temp17)}</ul> : <p className="text-sm text-muted-foreground">No documents were selected for upload.</p>}</div>;
      $[247] = documentUploadLabels;
      $[248] = t84;
    } else {
      t84 = $[248];
    }
    let t85;
    if ($[249] !== t81 || $[250] !== t84) {
      t85 = <div className="space-y-3">{t81}{t82}{t84}</div>;
      $[249] = t81;
      $[250] = t84;
      $[251] = t85;
    } else {
      t85 = $[251];
    }
    let t86;
    if ($[252] !== coverageReadback?.primary.selfPay || $[253] !== followThroughEligible || $[254] !== insuranceStatusDisplay) {
      t86 = followThroughEligible ? <DecisionMetadata items={[{
        label: "Athena eligibility",
        value: insuranceStatusDisplay
      }, {
        label: "Self pay",
        value: coverageReadback?.primary.selfPay ? "Yes" : "No"
      }]} /> : undefined;
      $[252] = coverageReadback?.primary.selfPay;
      $[253] = followThroughEligible;
      $[254] = insuranceStatusDisplay;
      $[255] = t86;
    } else {
      t86 = $[255];
    }
    let t87;
    if ($[256] !== coverageReadback?.primary.selfPay || $[257] !== followThroughEligible || $[258] !== insuranceRequirements || $[259] !== skippedFollowThroughSummary) {
      t87 = coverageReadback?.primary.selfPay ? "Network checks do not apply to Self Pay." : insuranceRequirements ? `Clinic: ${formatFromUnderscoreToHumanReadable(insuranceRequirements.clinicNetwork)}. Provider: ${formatFromUnderscoreToHumanReadable(insuranceRequirements.providerNetwork)}.` : followThroughEligible ? "Waiting for the confirmed package number and insurance-grid decision." : skippedFollowThroughSummary;
      $[256] = coverageReadback?.primary.selfPay;
      $[257] = followThroughEligible;
      $[258] = insuranceRequirements;
      $[259] = skippedFollowThroughSummary;
      $[260] = t87;
    } else {
      t87 = $[260];
    }
    let t88;
    if ($[261] !== insuranceRequirements) {
      t88 = insuranceRequirements ? <div className="space-y-3"><DecisionMetadata items={[{
          label: "Clinic",
          value: formatFromUnderscoreToHumanReadable(insuranceRequirements.clinicNetwork)
        }, {
          label: "Provider",
          value: formatFromUnderscoreToHumanReadable(insuranceRequirements.providerNetwork)
        }, {
          label: "Additional info",
          value: insuranceRequirements.additionalSchedulingInformationRequired ? "Required" : "No"
        }, {
          label: "PCP referral",
          value: insuranceRequirements.referralMustBeFromPcp ? "Required" : "No"
        }]} />{insuranceRequirements.reviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">{insuranceRequirements.reviewReasons.map(_temp18)}</ul> : null}</div> : undefined;
      $[261] = insuranceRequirements;
      $[262] = t88;
    } else {
      t88 = $[262];
    }
    let t89;
    if ($[263] !== insuranceRequirements || $[264] !== isPrimaryCareProvider) {
      t89 = insuranceRequirements ? <DecisionMetadata items={[{
        label: "PCP referral",
        value: insuranceRequirements.referralMustBeFromPcp ? "Required" : "No"
      }, {
        label: "Referring is PCP",
        value: isPrimaryCareProvider ? "Yes" : "No"
      }]} /> : undefined;
      $[263] = insuranceRequirements;
      $[264] = isPrimaryCareProvider;
      $[265] = t89;
    } else {
      t89 = $[265];
    }
    let t90;
    if ($[266] !== followThroughEligible || $[267] !== opsApproval?.metadata?.instruction || $[268] !== opsKind || $[269] !== referralAuthorization || $[270] !== skippedFollowThroughSummary) {
      t90 = opsKind === "authorization_review" ? opsApproval?.metadata?.instruction ?? "Review the Athena referral authorization object, then continue." : opsKind === "manual_authorization" ? opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION : referralAuthorization?.outcome === "created" ? `${referralAuthorization.write === "authorization" ? "Authorization" : "Paper referral"} object ${referralAuthorization.alreadyExisted ? "already existed" : "was created"} in Athena${referralAuthorization.pending ? " with number pending" : ""}.` : referralAuthorization?.outcome === "needs_pending_request" ? "No authorization number is on file. An outbound request letter is required next." : tprReferralAuthNeedsReview(referralAuthorization) ? "No Athena object was created because the authorization requirements need manual review." : referralAuthorization?.outcome === "not_needed" ? `No Athena object was needed (${formatFromUnderscoreToHumanReadable(referralAuthorization.reason)}).` : followThroughEligible ? "Waiting for network, authorization, and referral-requirement decisions." : skippedFollowThroughSummary;
      $[266] = followThroughEligible;
      $[267] = opsApproval?.metadata?.instruction;
      $[268] = opsKind;
      $[269] = referralAuthorization;
      $[270] = skippedFollowThroughSummary;
      $[271] = t90;
    } else {
      t90 = $[271];
    }
    let t91;
    if ($[272] !== insuranceRequirements || $[273] !== referralAuthorization) {
      t91 = insuranceRequirements ? <DecisionMetadata items={[{
        label: "Authorization",
        value: formatFromUnderscoreToHumanReadable(insuranceRequirements.authorizationRequirement)
      }, {
        label: "Referral",
        value: formatFromUnderscoreToHumanReadable(insuranceRequirements.referralRequirement)
      }, {
        label: "Outcome",
        value: referralAuthorization ? formatFromUnderscoreToHumanReadable(referralAuthorization.outcome) : "Pending"
      }, ...(referralAuthorization?.outcome === "created" ? [{
        label: "Athena object",
        value: referralAuthorization.referralAuthId
      }, {
        label: "Appointment",
        value: referralAuthorization.needsManualAppointmentAttach ? "Attach manually" : referralAuthorization.appointmentAttached ? "Attached" : "None in window"
      }] : [])]} /> : undefined;
      $[272] = insuranceRequirements;
      $[273] = referralAuthorization;
      $[274] = t91;
    } else {
      t91 = $[274];
    }
    let t92;
    if ($[275] !== authRequestUploaded || $[276] !== referralAuthNeedsPendingUpload) {
      t92 = referralAuthNeedsPendingUpload || authRequestUploaded ? <div className="space-y-3"><DecisionMetadata items={[{
          label: "Request letter",
          value: authRequestUploaded ? "Uploaded" : "Waiting for ops"
        }]} /><ol className="list-decimal space-y-1 pl-5 text-sm"><li>Export the patient chart in Athena. Do not skip this export.</li><li>Click Upload request letter. The letter is not uploaded automatically.</li><li>Athena then receives a Referral Auth object with number pending.</li></ol></div> : undefined;
      $[275] = authRequestUploaded;
      $[276] = referralAuthNeedsPendingUpload;
      $[277] = t92;
    } else {
      t92 = $[277];
    }
    let t93;
    if ($[278] !== actionNote || $[279] !== notesComplete || $[280] !== resolvedReceiving.providerLabel) {
      t93 = notesComplete ? <div className="space-y-3"><DecisionMetadata items={[{
          label: "Destinations",
          value: "Document + Patient Notes"
        }, {
          label: "Provider",
          value: resolvedReceiving.providerLabel || NOT_AVAILABLE
        }]} /><p className="text-sm"><strong>Final note:</strong> <CopyableContent text={actionNote} /></p></div> : undefined;
      $[278] = actionNote;
      $[279] = notesComplete;
      $[280] = resolvedReceiving.providerLabel;
      $[281] = t93;
    } else {
      t93 = $[281];
    }
    const t94 = bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? NO_CONFIDENT_BUCKET_SUGGESTION : SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel;
    let t95;
    if ($[282] !== t94) {
      t95 = {
        label: "Suggested bucket",
        value: t94
      };
      $[282] = t94;
      $[283] = t95;
    } else {
      t95 = $[283];
    }
    const t96 = hasMissingInformation ? "Required" : "No";
    let t97;
    if ($[284] !== t96) {
      t97 = {
        label: "Missing information",
        value: t96
      };
      $[284] = t96;
      $[285] = t97;
    } else {
      t97 = $[285];
    }
    const t98 = authRequestUploaded ? "Uploaded" : "Not required";
    let t99;
    if ($[286] !== t98) {
      t99 = {
        label: "Authorization request",
        value: t98
      };
      $[286] = t98;
      $[287] = t99;
    } else {
      t99 = $[287];
    }
    let t100;
    if ($[288] !== t95 || $[289] !== t97 || $[290] !== t99) {
      t100 = <div className="space-y-4"><DecisionMetadata items={[t95, t97, t99]} /></div>;
      $[288] = t95;
      $[289] = t97;
      $[290] = t99;
      $[291] = t100;
    } else {
      t100 = $[291];
    }
    steps = [{
      number: 1,
      title: "Locate the Document in the Patient Chart",
      status: ensurePatientStatus,
      summary: "The patient chart is created or updated without re-registering established patients.",
      taskStepKeys: [StepKey.ENSURE_PATIENT],
      content: t25
    }, {
      number: 2,
      title: "Verify the provider and location",
      status: assignmentConfirmationStatus,
      taskStepKeys: [StepKey.RESOLVE_RECEIVING_ASSIGNMENT, StepKey.TPR_CAMELBACK_FOLLOW_THROUGH],
      summary: usesRetiredUsualProviderRule ? "This legacy assignment used Athena's usual provider and must be checked against qualifying appointment history." : assignmentConfirmationStatus === "needs_action" ? "Provider and location require manual review before downstream referral work." : "Patient status, appointment history, provider, and location were evaluated together, then reconciled against the TPR roster.",
      content: t40,
      continuation: receivingAssignmentApproval ? {
        instruction: "After confirming the provider and location in Athena, continue the workflow.",
        control: <TprReceivingAssignmentContinueButton task={task} />
      } : undefined
    }, {
      number: 3,
      title: "Update demographics and care team",
      status: demographicsStatus,
      taskStepKeys: [StepKey.ENSURE_PATIENT, StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM],
      summary: "Review patient details, care team details, and emergency contact separately in Quickview.",
      content: t64
    }, {
      number: 4,
      title: "Insurance intake and coverage write",
      status: coverageStatus,
      taskStepKeys: [StepKey.CHECK_INSURANCE_ELIGIBILITY, StepKey.CREATE_COVERAGE, StepKey.CONFIRM_ATHENA_COVERAGE, StepKey.READ_ATHENA_COVERAGE],
      summary: coverageApproval ? coverageApproval.metadata?.instruction ?? "Confirm the Athena coverage before continuing." : coverageReadbackComplete ? "Athena coverage was confirmed and package numbers were persisted before referral processing." : assignmentRequiresManualReview ? "Not run because the receiving provider and location require manual confirmation." : "Waiting for the coverage write and required Ops confirmation.",
      content: t66,
      continuation: coverageApproval ? {
        instruction: "After verifying the coverage sequence in Athena, continue the workflow.",
        control: <TprCoverageContinueButton task={task} />
      } : undefined
    }, {
      number: 5,
      title: "Review Full Registration",
      status: fullRegistrationStatus,
      summary: "Open Full Registration and verify communication consent, privacy, and referral source.",
      taskStepKeys: [StepKey.ENSURE_PATIENT],
      content: t73
    }, {
      number: 6,
      title: "Process referral documents",
      status: taskStepStatus(taskSteps, [StepKey.REFERRAL_DOCUMENT_METADATA, StepKey.SUPPORTING_SECTIONS_UPLOAD]),
      summary: "Referral metadata and supporting sections are processed after insurance intake is complete.",
      taskStepKeys: [StepKey.REFERRAL_DOCUMENT_METADATA, StepKey.SUPPORTING_SECTIONS_UPLOAD],
      content: t85
    }, {
      number: 7,
      title: "Run Athena eligibility",
      status: athenaEligibilityStatus,
      taskStepKeys: [StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY, StepKey.SYNC_ATHENA_COVERAGES],
      summary: coverageReadback?.primary.selfPay ? "Self Pay has no payer eligibility check." : followThroughEligible ? `Athena eligibility result: ${insuranceStatusDisplay}. Athena, not Stedi, controls the final insurance note.` : skippedFollowThroughSummary,
      content: t86
    }, {
      number: 8,
      title: "Check clinic and provider network",
      status: networkStatus,
      taskStepKeys: [StepKey.RESOLVE_TPR_INSURANCE_REQUIREMENTS],
      summary: t87,
      content: t88
    }, {
      number: 9,
      title: "Send PCP referral fax if required",
      status: pcpFaxStatus,
      taskStepKeys: [StepKey.TPR_PCP_REFERRAL_FAX],
      summary: opsKind === "pcp_referral_fax" ? opsApproval?.metadata?.instruction ?? "This plan requires the referral to come from the PCP. Export the chart and send the outbound fax." : insuranceRequirements?.referralMustBeFromPcp ? "The plan requires a PCP referral. The referring provider was checked against the TPR PCP roster." : followThroughEligible ? "This plan does not require the referral to come from the PCP." : skippedFollowThroughSummary,
      content: t89,
      continuation: opsKind === "pcp_referral_fax" ? {
        instruction: opsApproval?.metadata?.instruction ?? "After sending the outbound PCP referral fax from Athena, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="PCP referral fax confirmed" successDescription="The workflow is continuing with authorization." />
      } : undefined
    }, {
      number: 10,
      title: "Resolve authorization and referral requirements",
      status: authorizationStatus,
      taskStepKeys: [StepKey.TPR_REFERRAL_AUTHORIZATION],
      summary: t90,
      content: t91,
      continuation: opsKind === "authorization_review" ? {
        instruction: opsApproval?.metadata?.instruction ?? "After reviewing the Athena referral authorization object, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="Authorization reviewed" successDescription="The workflow is continuing with the remaining referral steps." />
      } : opsKind === "manual_authorization" ? {
        instruction: opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION,
        control: <TprOpsContinueButton task={task} successTitle="Manual authorization recorded" successDescription="The workflow is continuing with the remaining referral steps." />
      } : undefined
    }, {
      number: 11,
      title: "Upload authorization request letter",
      status: authRequestUploadStatus,
      taskStepKeys: [StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD],
      summary: opsKind === "auth_request_upload" ? "Export the patient chart in Athena manually, then upload the request letter. The letter is not uploaded automatically." : authRequestUploaded ? "The authorization-request letter was uploaded after ops confirmed." : referralAuthNeedsPendingUpload ? "Waiting to upload the request letter after ops confirms the chart was exported." : followThroughEligible ? "Not required unless the authorization number is missing." : skippedFollowThroughSummary,
      content: t92,
      continuation: opsKind === "auth_request_upload" ? {
        instruction: "After exporting the chart in Athena, upload the request letter.",
        control: <TprOpsContinueButton task={task} label="Upload request letter" pendingLabel="Uploading..." successTitle="Request letter queued" successDescription="The workflow will write the pending authorization and upload the letter." />
      } : undefined
    }, {
      number: 12,
      title: "Write unified notes",
      status: dispositionStatus,
      summary: "After all decisions are complete, a final summary is added to the document and Patient Notes.",
      taskStepKeys: [StepKey.REFERRAL_DISPOSITION],
      content: t93
    }, {
      number: 13,
      title: "Close document if there is an upcoming appointment",
      status: closeDocumentStatus,
      taskStepKeys: [StepKey.TPR_CLOSE_DOCUMENT],
      summary: opsKind === "close_document" ? opsApproval?.metadata?.instruction ?? "There is an upcoming appointment within 90 days. Close the document in Athena, then continue." : followThroughEligible ? "Closes the Athena document only when an upcoming appointment exists within 90 days. This cannot be done via API." : skippedFollowThroughSummary,
      continuation: opsKind === "close_document" ? {
        instruction: "After closing the document in Athena, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="Document close confirmed" successDescription="The workflow is continuing with remaining follow-through." />
      } : undefined
    }, {
      number: 14,
      title: "Complete manual follow-through and routing",
      taskStepKeys: [StepKey.SCHEDULING_RECOMMENDATION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX],
      status: dispositionStatus === "failed" ? "not_run" : !workflowStillProcessing && notesComplete && (!hasMissingInformation || missingInfoUploadComplete) ? "needs_action" : taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX]),
      summary: hasMissingInformation && !missingInfoUploadComplete ? "Waiting for the missing-information document to be prepared." : referralAuthNeedsPendingUpload && authRequestUploaded ? "Confirm the authorization-request letter on the chart, route, and complete." : hasMissingInformation ? "Send the missing-information document, route the referral, and complete only after all manual actions are done." : "Confirm the recommended destination bucket and complete the task.",
      content: t100,
      continuation: verificationActionStepNumber === 14 ? {
        instruction: `After completing the manual follow-through in Athena and assigning ${bucketLabel}, continue to complete the task.`,
        control: <VerificationButtons task={task} continueOnly={true} />
      } : undefined
    }];
    let t101;
    if ($[292] !== taskSteps) {
      t101 = taskSteps.find(_temp19);
      $[292] = taskSteps;
      $[293] = t101;
    } else {
      t101 = $[293];
    }
    const failedTaskStep = t101;
    failedStepCard = failedTaskStep ? steps.find(step_6 => step_6.taskStepKeys.some(stepKey => stepKey === failedTaskStep.stepKey)) : undefined;
    t3 = failedTaskStep && failedStepCard ? failedStepCard.taskStepKeys.find(stepKey_0 => stepKey_0 === failedTaskStep.stepKey) ?? null : null;
    $[6] = actionNote;
    $[7] = bucketLabel;
    $[8] = coverageReadback;
    $[9] = documentId;
    $[10] = documentUploadLabels;
    $[11] = followThroughEligible;
    $[12] = hasMissingInformation;
    $[13] = insuranceRequirements;
    $[14] = insuranceStatusDisplay;
    $[15] = isPrimaryCareProvider;
    $[16] = isProviderFirst;
    $[17] = opsCrossCheck.diagnosis;
    $[18] = opsCrossCheck.emergencyContactName;
    $[19] = opsCrossCheck.emergencyContactPhoneNumber;
    $[20] = opsCrossCheck.emergencyContactRelationship;
    $[21] = opsCrossCheck.patientDateOfBirth;
    $[22] = opsCrossCheck.patientHomeAddress;
    $[23] = opsCrossCheck.patientName;
    $[24] = opsCrossCheck.patientPhoneNumber;
    $[25] = opsCrossCheck.primaryPlanLabel;
    $[26] = opsCrossCheck.primarySubscriberId;
    $[27] = opsCrossCheck.receivingProviderName;
    $[28] = opsCrossCheck.receivingProviderNpi;
    $[29] = opsCrossCheck.referralDate;
    $[30] = opsCrossCheck.referringLocationAddress;
    $[31] = opsCrossCheck.referringLocationName;
    $[32] = opsCrossCheck.referringProviderName;
    $[33] = opsCrossCheck.referringProviderNpi;
    $[34] = opsCrossCheck.referringProviderSpecialty;
    $[35] = patientDisplayId;
    $[36] = patientEhrId;
    $[37] = patientLabel;
    $[38] = pendingApproval;
    $[39] = primaryCareProviderAlreadyPresent;
    $[40] = privacyNoticeOnFile;
    $[41] = providerSelectionEvidence;
    $[42] = receivingAssignmentConfirmed;
    $[43] = referralAuthorization;
    $[44] = referralId;
    $[45] = resolvedReceiving.flags;
    $[46] = resolvedReceiving.locationLabel;
    $[47] = resolvedReceiving.providerLabel;
    $[48] = resolvedReceiving.providerNpi;
    $[49] = resolvedReceiving.providerWriteSafe;
    $[50] = resolvedReceiving.usualProviderWriteSafe;
    $[51] = task;
    $[52] = taskSteps;
    $[53] = unresolvedUpcomingProviders;
    $[54] = upcomingAppointmentProviderSummary;
    $[55] = workflowStillProcessing;
    $[56] = coverageApproval;
    $[57] = failedStepCard;
    $[58] = opsApproval;
    $[59] = steps;
    $[60] = t3;
    $[61] = waitingForAssociatedReferral;
  } else {
    coverageApproval = $[56];
    failedStepCard = $[57];
    opsApproval = $[58];
    steps = $[59];
    t3 = $[60];
    waitingForAssociatedReferral = $[61];
  }
  const failedStepKey = t3;
  let t4;
  if ($[294] !== coverageApproval || $[295] !== failedStepCard?.number || $[296] !== opsApproval || $[297] !== steps) {
    t4 = coverageApproval ? steps.filter(_temp20) : opsApproval ? steps.filter(_temp21) : steps.filter(step_9 => step_9.number === failedStepCard?.number || step_9.status === "needs_action" || step_9.status === "failed" || step_9.status === "verify");
    $[294] = coverageApproval;
    $[295] = failedStepCard?.number;
    $[296] = opsApproval;
    $[297] = steps;
    $[298] = t4;
  } else {
    t4 = $[298];
  }
  const nextSteps = t4;
  const actionsRemainingVariant = failedStepCard || nextSteps.some(_temp22) ? "destructive" : nextSteps.length || waitingForAssociatedReferral ? "warn" : workflowStillProcessing ? "outline" : "success";
  let t5;
  if ($[299] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ListChecks />;
    $[299] = t5;
  } else {
    t5 = $[299];
  }
  let t6;
  if ($[300] !== failedStepCard?.number || $[301] !== nextSteps || $[302] !== waitingForAssociatedReferral || $[303] !== workflowStillProcessing) {
    t6 = waitingForAssociatedReferral ? <p className="text-sm">Waiting for a matching Referral for this patient. Standalone Referral Auth holds until a Referral in the two-day window finishes syncing.</p> : nextSteps.length ? <ol className="space-y-2 text-sm">{nextSteps.map(step_11 => <li key={step_11.number}><strong>Step {step_11.number}: {step_11.title}</strong><span className="block text-muted-foreground">{step_11.number === failedStepCard?.number ? "Complete this step manually, then continue from its card." : step_11.summary}</span></li>)}</ol> : <p className="text-sm">{workflowStillProcessing ? "Automation is still running. This timeline will update as each durable workflow step completes." : "No manual actions remain."}</p>;
    $[300] = failedStepCard?.number;
    $[301] = nextSteps;
    $[302] = waitingForAssociatedReferral;
    $[303] = workflowStillProcessing;
    $[304] = t6;
  } else {
    t6 = $[304];
  }
  let t7;
  if ($[305] !== actionsRemainingVariant || $[306] !== t6) {
    t7 = <ValerieCard title="Actions remaining" icon={t5} variant={actionsRemainingVariant} contentClassName="space-y-4">{t6}</ValerieCard>;
    $[305] = actionsRemainingVariant;
    $[306] = t6;
    $[307] = t7;
  } else {
    t7 = $[307];
  }
  let t8;
  if ($[308] !== failedStepCard?.number || $[309] !== failedStepKey || $[310] !== steps || $[311] !== task) {
    t8 = steps.map(step_12 => <TimelineStepCard key={step_12.number} task={task} step={step_12} failedStepKey={step_12.number === failedStepCard?.number ? failedStepKey : null} />);
    $[308] = failedStepCard?.number;
    $[309] = failedStepKey;
    $[310] = steps;
    $[311] = task;
    $[312] = t8;
  } else {
    t8 = $[312];
  }
  let t9;
  if ($[313] !== t7 || $[314] !== t8) {
    t9 = <div className="space-y-8 pb-8">{t7}{t8}</div>;
    $[313] = t7;
    $[314] = t8;
    $[315] = t9;
  } else {
    t9 = $[315];
  }
  return t9;
}
function _temp22(step_10) {
  return step_10.status === "failed";
}
function _temp21(step_8) {
  return step_8.continuation;
}
function _temp20(step_7) {
  return step_7.number === 4;
}
function _temp19(step_5) {
  return step_5.status === TaskStepStatus.FAILED;
}
function _temp18(reason_4) {
  return <li key={reason_4}>{reason_4}</li>;
}
function _temp17(label) {
  return <li key={label}>{label}</li>;
}
function _temp16(reason_3) {
  return <li key={reason_3}>{reason_3}</li>;
}
function _temp15(reason_2) {
  return <li key={reason_2}>{reason_2}</li>;
}
function _temp14(provider) {
  return <Badge key={provider.providerEhrId ?? provider.label} variant="outline">{provider.label}{provider.providerEhrId ? ` · Athena ID ${provider.providerEhrId}` : ""}</Badge>;
}
function _temp13(reason_1) {
  return <li key={reason_1}>{reason_1}</li>;
}
function _temp12(coverage) {
  return <p key={coverage.insuranceId}><strong>{coverage.casePolicy ? "Case policy" : coverage.sequence === 1 ? "Primary" : coverage.sequence === 2 ? "Secondary" : `Sequence ${coverage.sequence ?? "N/A"}`}:</strong>{" "}{coverage.planName ?? "Unknown plan"} · Package <CopyableContent text={coverage.insurancePackageId} />{coverage.subscriberId ? <>{" "}· Subscriber <CopyableContent text={coverage.subscriberId} /></> : null}</p>;
}
function _temp11(reason_0) {
  return reason_0 !== null;
}
function _temp10(reason) {
  return reason !== null;
}
function _temp9(file) {
  return file.type === FileType.INBOUND;
}
function _temp8(step_4) {
  return step_4.stepKey === StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX && step_4.status === TaskStepStatus.COMPLETED;
}
function _temp7(step_3) {
  return step_3.stepKey === StepKey.REFERRAL_DISPOSITION && step_3.status === TaskStepStatus.COMPLETED;
}
function _temp6(step_2) {
  return step_2.stepKey === StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD && step_2.status === TaskStepStatus.COMPLETED && step_2.output?.uploaded === true;
}
function _temp5(step_1) {
  return step_1.stepKey === StepKey.READ_ATHENA_COVERAGE && step_1.status === TaskStepStatus.COMPLETED;
}
function _temp4(step_0) {
  return step_0.stepKey === StepKey.TPR_WAIT_FOR_ASSOCIATED_REFERRAL && step_0.status === TaskStepStatus.PAUSED;
}
function _temp3(step) {
  return step.stepKey === StepKey.RESOLVE_RECEIVING_ASSIGNMENT;
}
interface TprReferralInstructionsProps {
  view: "information" | "steps";
  referral: Referral;
  task: Task;
  athenaData: AthenaReferralCardsData | null;
  patientEhrId: string;
  /** "New Patient Referral" / "Established Patient Referral" / "N/A" — see getAthenaReferralDocumentLabel. */
  patientLabel: string;
  upcomingAppointments: Appointment[];
  /** Past booked appointments (lookback window matching the new/established determination) for the
   * "Patient Type" sanity-check list — display-only, not the source of the New/Established label itself. */
  pastAppointments: Appointment[];
  documentId: string;
  isPrimaryCareProvider: boolean;
  /** Whether the referring provider is already on the Athena care team as PRIMARY_CARE_PROVIDER —
   * suppresses the redundant "PCP" line in Step 3's care team card. See resolveTprCareTeamPolicy. */
  primaryCareProviderAlreadyPresent: boolean;
  /** One of TprSyncBucket's four values: "closed" | "sscardio" | "biltmorecardreferral" | "biltmorecardiosched" */
  bucketLabel: string;
  actionNote: string;
  /** Each decision-tree check that ran, in order, with its outcome — see resolveTprSyncChecklist. */
  decisionTrace: TprSyncChecklistTraceEntry[];
  /** Non-stopping context notes from the decision tree (insurance-sync issues, urgency reason, the
   * detailed "ops must manually verify..." explanation behind a short bucket-assignment label, etc). */
  flags: string[];
  /** "Eligible" | "Ineligible" | "Unverified" — same Athena-then-Stedi result the decision tree reads. */
  eligibilityDisplay: StediEligibilityDisplayLabel;
  documentUploadLabels: string[];
  privacyNoticeOnFile: boolean | null;
  taskSteps: TaskStep[];
  pendingApproval: TaskApproval | null;
  receivingAssignmentConfirmed: boolean;
  followThroughEligible: boolean;
  coverageReadback: AthenaCoverageReadbackOutput | null;
  insuranceRequirements: TprInsuranceRequirementsOutput | null;
  referralAuthorization: TprReferralAuthorizationOutput | null;
  unresolvedUpcomingProviders: Array<{
    label: string;
    providerEhrId: string | null;
  }>;
  upcomingAppointmentProviderSummary: string | null;
  /** Persisted provider-first assignment, or the legacy display fallback for older tasks. */
  resolvedReceiving: {
    providerLabel: string | null;
    providerNpi: string | null;
    providerWriteSafe: boolean;
    usualProviderWriteSafe: boolean;
    locationLabel: string | null;
    trace: string[];
    flags: string[];
    routedToSscardio: boolean;
    providerSelectionEvidence?: ReceivingAssignmentOutput["providerSelectionEvidence"];
  };
}
export function TprReferralInstructions(t0) {
  const $ = _c(399);
  const {
    view,
    referral,
    task,
    athenaData,
    patientEhrId,
    patientLabel,
    upcomingAppointments,
    pastAppointments,
    documentId,
    isPrimaryCareProvider,
    primaryCareProviderAlreadyPresent,
    bucketLabel,
    actionNote,
    decisionTrace,
    flags,
    eligibilityDisplay,
    documentUploadLabels,
    privacyNoticeOnFile,
    taskSteps,
    pendingApproval,
    receivingAssignmentConfirmed,
    followThroughEligible,
    coverageReadback,
    insuranceRequirements,
    referralAuthorization,
    unresolvedUpcomingProviders,
    upcomingAppointmentProviderSummary,
    resolvedReceiving
  } = t0;
  let t1;
  if ($[0] !== referral.referringLocation?.address) {
    t1 = getFormattedAddress(referral.referringLocation?.address) || NOT_AVAILABLE;
    $[0] = referral.referringLocation?.address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const referringLocationAddress = t1;
  const referringProviderSpecialty = referral.referringProvider?.specialty || NOT_AVAILABLE;
  let t2;
  if ($[2] !== referral.referringProvider) {
    t2 = referral.referringProvider ? getPractitionerNameWithCredentials(referral.referringProvider) : NOT_AVAILABLE;
    $[2] = referral.referringProvider;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const referringProviderName = t2;
  let t3;
  if ($[4] !== referral.receivingProvider) {
    t3 = referral.receivingProvider ? getPractitionerNameWithCredentials(referral.receivingProvider) : NOT_AVAILABLE;
    $[4] = referral.receivingProvider;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const receivingProviderName = t3;
  const patient = referral.patient;
  const emergencyContactName = patient?.emergencyContactName || NOT_AVAILABLE;
  const emergencyContactPhoneNumber = patient?.emergencyContactPhoneNumber || NOT_AVAILABLE;
  const emergencyContactRelationship = patient?.emergencyContactRelationshipToPatient || NOT_AVAILABLE;
  let t4;
  if ($[6] !== referral.primaryCoverage) {
    t4 = getCoverageSummary(referral.primaryCoverage);
    $[6] = referral.primaryCoverage;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const primaryCoverageSummary = t4;
  let t5;
  if ($[8] !== referral.secondaryCoverage) {
    t5 = getCoverageSummary(referral.secondaryCoverage);
    $[8] = referral.secondaryCoverage;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const secondaryCoverageSummary = t5;
  let t6;
  if ($[10] !== referral.dateOfReferral) {
    t6 = formatDateMmDdYyyy(referral.dateOfReferral) || NOT_AVAILABLE;
    $[10] = referral.dateOfReferral;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const dateOfReferral = t6;
  const schedulingPriority = referral.schedulingPriority || NOT_AVAILABLE;
  const internalNoteValue = athenaData?.document?.internalNote || NOT_AVAILABLE;
  let T0;
  let dataSourceEntries;
  let hasMissingInformation;
  let missingFields;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t7;
  let t8;
  let t9;
  if ($[12] !== athenaData?.coverages || $[13] !== athenaData?.patientDetails || $[14] !== bucketLabel || $[15] !== coverageReadback || $[16] !== dateOfReferral || $[17] !== documentId || $[18] !== documentUploadLabels || $[19] !== eligibilityDisplay || $[20] !== emergencyContactName || $[21] !== emergencyContactPhoneNumber || $[22] !== emergencyContactRelationship || $[23] !== followThroughEligible || $[24] !== insuranceRequirements || $[25] !== internalNoteValue || $[26] !== isPrimaryCareProvider || $[27] !== pastAppointments || $[28] !== patient || $[29] !== patientEhrId || $[30] !== patientLabel || $[31] !== pendingApproval || $[32] !== primaryCareProviderAlreadyPresent || $[33] !== primaryCoverageSummary || $[34] !== privacyNoticeOnFile || $[35] !== receivingAssignmentConfirmed || $[36] !== receivingProviderName || $[37] !== referral.diagnosisList || $[38] !== referral.id || $[39] !== referral.primaryCoverage || $[40] !== referral.reasonForReferral || $[41] !== referral.receivingLocation?.name || $[42] !== referral.receivingProvider || $[43] !== referral.referringLocation?.name || $[44] !== referral.referringProvider || $[45] !== referralAuthorization || $[46] !== referringLocationAddress || $[47] !== referringProviderName || $[48] !== referringProviderSpecialty || $[49] !== resolvedReceiving || $[50] !== secondaryCoverageSummary || $[51] !== task || $[52] !== taskSteps || $[53] !== unresolvedUpcomingProviders || $[54] !== upcomingAppointmentProviderSummary || $[55] !== upcomingAppointments || $[56] !== view) {
    t25 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const sortedPastAppointments = sortAppointmentsMostRecentFirst(pastAppointments);
      const syncFailed = task.status === TaskStatus.FAILED;
      const insuranceStatusDisplay = coverageReadback?.primary.selfPay ? "Self Pay" : referral.primaryCoverage ? eligibilityDisplay : "Missing Insurance";
      const chartPatient = athenaData?.patientDetails ?? null;
      let t26;
      if ($[80] !== patient) {
        t26 = patient && getPatientPhoneNumber(patient)?.trim() || null;
        $[80] = patient;
        $[81] = t26;
      } else {
        t26 = $[81];
      }
      const referralPhone = t26;
      let t27;
      if ($[82] !== chartPatient) {
        t27 = chartPatient && getPatientPhoneNumber(chartPatient)?.trim() || null;
        $[82] = chartPatient;
        $[83] = t27;
      } else {
        t27 = $[83];
      }
      const chartPhone = t27;
      let chartAddressValue;
      let phoneDataSourceEntry;
      let referralAddressValue;
      let t28;
      let t29;
      let t30;
      if ($[84] !== athenaData?.coverages || $[85] !== chartPatient || $[86] !== chartPhone || $[87] !== patient || $[88] !== referral.primaryCoverage || $[89] !== referralPhone) {
        const referralCoverageValue = getCoverageSummary(referral.primaryCoverage);
        const chartPrimaryCoverage = [...(athenaData?.coverages ?? [])].sort(_temp23)[0];
        const chartCoverageValue = getCoverageSummary(chartPrimaryCoverage ?? null);
        referralAddressValue = patient?.homeAddress ? getFormattedAddress(patient.homeAddress) : null;
        chartAddressValue = chartPatient?.homeAddress ? getFormattedAddress(chartPatient.homeAddress) : null;
        let t31;
        if ($[96] !== chartPhone || $[97] !== referralPhone) {
          t31 = resolveDataSourceEntry("Phone Number", referralPhone, chartPhone);
          $[96] = chartPhone;
          $[97] = referralPhone;
          $[98] = t31;
        } else {
          t31 = $[98];
        }
        phoneDataSourceEntry = t31;
        t28 = phoneDataSourceEntry;
        t29 = resolveDataSourceEntry("Insurance", referralCoverageValue, chartCoverageValue);
        t30 = resolveDataSourceEntry("Home Address", referralAddressValue, chartAddressValue);
        $[84] = athenaData?.coverages;
        $[85] = chartPatient;
        $[86] = chartPhone;
        $[87] = patient;
        $[88] = referral.primaryCoverage;
        $[89] = referralPhone;
        $[90] = chartAddressValue;
        $[91] = phoneDataSourceEntry;
        $[92] = referralAddressValue;
        $[93] = t28;
        $[94] = t29;
        $[95] = t30;
      } else {
        chartAddressValue = $[90];
        phoneDataSourceEntry = $[91];
        referralAddressValue = $[92];
        t28 = $[93];
        t29 = $[94];
        t30 = $[95];
      }
      let t31;
      if ($[99] !== t28 || $[100] !== t29 || $[101] !== t30) {
        t31 = [t28, t29, t30];
        $[99] = t28;
        $[100] = t29;
        $[101] = t30;
        $[102] = t31;
      } else {
        t31 = $[102];
      }
      dataSourceEntries = t31;
      const isCamelbackReferral = !resolvedReceiving.routedToSscardio;
      let t32;
      if ($[103] !== isCamelbackReferral || $[104] !== phoneDataSourceEntry) {
        t32 = isCamelbackReferral && phoneDataSourceEntry.missing ? [phoneDataSourceEntry] : [];
        $[103] = isCamelbackReferral;
        $[104] = phoneDataSourceEntry;
        $[105] = t32;
      } else {
        t32 = $[105];
      }
      missingFields = t32;
      hasMissingInformation = missingFields.length > 0;
      let t33;
      if ($[106] !== patient) {
        t33 = getPatientDisplayName(patient);
        $[106] = patient;
        $[107] = t33;
      } else {
        t33 = $[107];
      }
      const patientDisplayName = t33;
      const receivingLocationName = referral.receivingLocation?.name || NOT_AVAILABLE;
      let t34;
      if ($[108] !== referral.diagnosisList || $[109] !== referral.reasonForReferral) {
        t34 = [referral.reasonForReferral, ...((referral.diagnosisList ?? []) as DiagnosisItem[]).map(_temp24)].filter(_temp25).filter(_temp26).join(", ") || NOT_AVAILABLE;
        $[108] = referral.diagnosisList;
        $[109] = referral.reasonForReferral;
        $[110] = t34;
      } else {
        t34 = $[110];
      }
      const diagnosis = t34;
      let t35;
      if ($[111] !== patient) {
        t35 = patient ? getPatientName(patient) || NOT_AVAILABLE : NOT_AVAILABLE;
        $[111] = patient;
        $[112] = t35;
      } else {
        t35 = $[112];
      }
      let t36;
      if ($[113] !== patient?.dateOfBirth) {
        t36 = formatDateMmDdYyyy(patient?.dateOfBirth) || NOT_AVAILABLE;
        $[113] = patient?.dateOfBirth;
        $[114] = t36;
      } else {
        t36 = $[114];
      }
      const t37 = chartPhone ?? referralPhone ?? NOT_AVAILABLE;
      const t38 = chartAddressValue ?? referralAddressValue ?? NOT_AVAILABLE;
      const t39 = referral.receivingProvider?.npiNumber || NOT_AVAILABLE;
      const t40 = referral.referringProvider?.npiNumber || NOT_AVAILABLE;
      const t41 = referral.referringLocation?.name || NOT_AVAILABLE;
      let t42;
      if ($[115] !== referral.primaryCoverage) {
        t42 = referral.primaryCoverage ? getCoverageDisplayName({
          coverage: referral.primaryCoverage,
          includesPlan: true,
          includeSubscriberName: false
        }) : "Self Pay";
        $[115] = referral.primaryCoverage;
        $[116] = t42;
      } else {
        t42 = $[116];
      }
      const t43 = referral.primaryCoverage?.subscriberId || NOT_AVAILABLE;
      let t44;
      if ($[117] !== dateOfReferral || $[118] !== diagnosis || $[119] !== emergencyContactName || $[120] !== emergencyContactPhoneNumber || $[121] !== emergencyContactRelationship || $[122] !== receivingProviderName || $[123] !== referringLocationAddress || $[124] !== referringProviderName || $[125] !== referringProviderSpecialty || $[126] !== t35 || $[127] !== t36 || $[128] !== t37 || $[129] !== t38 || $[130] !== t39 || $[131] !== t40 || $[132] !== t41 || $[133] !== t42 || $[134] !== t43) {
        t44 = {
          patientName: t35,
          patientDateOfBirth: t36,
          patientPhoneNumber: t37,
          patientHomeAddress: t38,
          receivingProviderName,
          receivingProviderNpi: t39,
          referringProviderName,
          referringProviderNpi: t40,
          referringProviderSpecialty,
          referringLocationName: t41,
          referringLocationAddress,
          emergencyContactName,
          emergencyContactPhoneNumber,
          emergencyContactRelationship,
          referralDate: dateOfReferral,
          primaryPlanLabel: t42,
          primarySubscriberId: t43,
          diagnosis
        };
        $[117] = dateOfReferral;
        $[118] = diagnosis;
        $[119] = emergencyContactName;
        $[120] = emergencyContactPhoneNumber;
        $[121] = emergencyContactRelationship;
        $[122] = receivingProviderName;
        $[123] = referringLocationAddress;
        $[124] = referringProviderName;
        $[125] = referringProviderSpecialty;
        $[126] = t35;
        $[127] = t36;
        $[128] = t37;
        $[129] = t38;
        $[130] = t39;
        $[131] = t40;
        $[132] = t41;
        $[133] = t42;
        $[134] = t43;
        $[135] = t44;
      } else {
        t44 = $[135];
      }
      const opsCrossCheck = t44;
      if (view === "steps") {
        let t45;
        if ($[136] !== bucketLabel || $[137] !== coverageReadback || $[138] !== documentId || $[139] !== documentUploadLabels || $[140] !== followThroughEligible || $[141] !== hasMissingInformation || $[142] !== insuranceRequirements || $[143] !== insuranceStatusDisplay || $[144] !== internalNoteValue || $[145] !== isPrimaryCareProvider || $[146] !== opsCrossCheck || $[147] !== pastAppointments || $[148] !== patient.displayId || $[149] !== patientEhrId || $[150] !== patientLabel || $[151] !== pendingApproval || $[152] !== primaryCareProviderAlreadyPresent || $[153] !== privacyNoticeOnFile || $[154] !== receivingAssignmentConfirmed || $[155] !== referral.id || $[156] !== referralAuthorization || $[157] !== resolvedReceiving || $[158] !== task || $[159] !== taskSteps || $[160] !== unresolvedUpcomingProviders || $[161] !== upcomingAppointmentProviderSummary || $[162] !== upcomingAppointments) {
          t45 = <div className="flex w-full max-w-2xl flex-col gap-8 sm:min-w-[800px] mx-auto"><TprSyncTimeline task={task} referralId={referral.id} taskSteps={taskSteps} pendingApproval={pendingApproval} receivingAssignmentConfirmed={receivingAssignmentConfirmed} followThroughEligible={followThroughEligible} coverageReadback={coverageReadback} insuranceRequirements={insuranceRequirements} referralAuthorization={referralAuthorization} resolvedReceiving={resolvedReceiving} patientLabel={patientLabel} patientEhrId={patientEhrId} documentId={documentId} bucketLabel={bucketLabel} actionNote={internalNoteValue} insuranceStatusDisplay={insuranceStatusDisplay} hasMissingInformation={hasMissingInformation} unresolvedUpcomingProviders={unresolvedUpcomingProviders} upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary} upcomingAppointments={upcomingAppointments} pastAppointments={pastAppointments} documentUploadLabels={documentUploadLabels} privacyNoticeOnFile={privacyNoticeOnFile} isPrimaryCareProvider={isPrimaryCareProvider} primaryCareProviderAlreadyPresent={primaryCareProviderAlreadyPresent} patientDisplayId={patient.displayId} opsCrossCheck={opsCrossCheck} /></div>;
          $[136] = bucketLabel;
          $[137] = coverageReadback;
          $[138] = documentId;
          $[139] = documentUploadLabels;
          $[140] = followThroughEligible;
          $[141] = hasMissingInformation;
          $[142] = insuranceRequirements;
          $[143] = insuranceStatusDisplay;
          $[144] = internalNoteValue;
          $[145] = isPrimaryCareProvider;
          $[146] = opsCrossCheck;
          $[147] = pastAppointments;
          $[148] = patient.displayId;
          $[149] = patientEhrId;
          $[150] = patientLabel;
          $[151] = pendingApproval;
          $[152] = primaryCareProviderAlreadyPresent;
          $[153] = privacyNoticeOnFile;
          $[154] = receivingAssignmentConfirmed;
          $[155] = referral.id;
          $[156] = referralAuthorization;
          $[157] = resolvedReceiving;
          $[158] = task;
          $[159] = taskSteps;
          $[160] = unresolvedUpcomingProviders;
          $[161] = upcomingAppointmentProviderSummary;
          $[162] = upcomingAppointments;
          $[163] = t45;
        } else {
          t45 = $[163];
        }
        t25 = t45;
        break bb0;
      }
      t24 = "flex flex-col gap-8 sm:min-w-[800px] w-full max-w-2xl mx-auto";
      T0 = EhrInstructionsCard;
      t23 = "Detailed Athena verification reference";
      t16 = "text-sm text-muted-foreground space-y-6";
      if ($[164] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = <h3 className="text-md font-semibold text-center text-foreground">TPR Biltmore Cardiology Sync Verification</h3>;
        $[164] = t17;
      } else {
        t17 = $[164];
      }
      const t45 = syncFailed ? "Automation did not complete for this task. Resolve the sync error below and verify Athena before closing." : "Automation has already run for this task. Use this checklist to verify the sync in Athena before closing.";
      if ($[165] !== t45) {
        t18 = <p className="text-xs text-center">{t45}</p>;
        $[165] = t45;
        $[166] = t18;
      } else {
        t18 = $[166];
      }
      if ($[167] !== task.id) {
        t19 = <SyncIncompleteActions taskId={task.id} />;
        $[167] = task.id;
        $[168] = t19;
      } else {
        t19 = $[168];
      }
      let t46;
      if ($[169] === Symbol.for("react.memo_cache_sentinel")) {
        t46 = <strong>Camelback Referral:</strong>;
        $[169] = t46;
      } else {
        t46 = $[169];
      }
      const t47 = isCamelbackReferral ? "Yes" : "No";
      let t48;
      if ($[170] !== t47) {
        t48 = <p>{t46} {t47}</p>;
        $[170] = t47;
        $[171] = t48;
      } else {
        t48 = $[171];
      }
      let t49;
      if ($[172] === Symbol.for("react.memo_cache_sentinel")) {
        t49 = <strong>Patient:</strong>;
        $[172] = t49;
      } else {
        t49 = $[172];
      }
      let t50;
      if ($[173] !== patientDisplayName) {
        t50 = <CopyableContent text={patientDisplayName} />;
        $[173] = patientDisplayName;
        $[174] = t50;
      } else {
        t50 = $[174];
      }
      let t51;
      if ($[175] !== patientLabel || $[176] !== t50) {
        t51 = <p>{t49} {t50} ({patientLabel})</p>;
        $[175] = patientLabel;
        $[176] = t50;
        $[177] = t51;
      } else {
        t51 = $[177];
      }
      let t52;
      if ($[178] === Symbol.for("react.memo_cache_sentinel")) {
        t52 = <strong>Receiving Provider (as extracted):</strong>;
        $[178] = t52;
      } else {
        t52 = $[178];
      }
      let t53;
      if ($[179] !== referral.receivingProvider) {
        t53 = <ProviderCopyFields provider={referral.receivingProvider} />;
        $[179] = referral.receivingProvider;
        $[180] = t53;
      } else {
        t53 = $[180];
      }
      let t54;
      if ($[181] === Symbol.for("react.memo_cache_sentinel")) {
        t54 = <strong>Location:</strong>;
        $[181] = t54;
      } else {
        t54 = $[181];
      }
      let t55;
      if ($[182] !== receivingLocationName) {
        t55 = <CopyableContent text={receivingLocationName} />;
        $[182] = receivingLocationName;
        $[183] = t55;
      } else {
        t55 = $[183];
      }
      let t56;
      if ($[184] !== t53 || $[185] !== t55) {
        t56 = <p>{t52}{" "}{t53}, {t54}{" "}{t55}</p>;
        $[184] = t53;
        $[185] = t55;
        $[186] = t56;
      } else {
        t56 = $[186];
      }
      if ($[187] !== t48 || $[188] !== t51 || $[189] !== t56) {
        t20 = <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">{t48}{t51}{t56}</div>;
        $[187] = t48;
        $[188] = t51;
        $[189] = t56;
        $[190] = t20;
      } else {
        t20 = $[190];
      }
      let t57;
      let t58;
      if ($[191] === Symbol.for("react.memo_cache_sentinel")) {
        t57 = <p className="font-semibold">Recommended Receiving Provider / Location</p>;
        t58 = <strong>Provider:</strong>;
        $[191] = t57;
        $[192] = t58;
      } else {
        t57 = $[191];
        t58 = $[192];
      }
      const t59 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
      let t60;
      if ($[193] !== t59) {
        t60 = <CopyableContent text={t59} />;
        $[193] = t59;
        $[194] = t60;
      } else {
        t60 = $[194];
      }
      let t61;
      if ($[195] === Symbol.for("react.memo_cache_sentinel")) {
        t61 = <strong>Location:</strong>;
        $[195] = t61;
      } else {
        t61 = $[195];
      }
      const t62 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
      let t63;
      if ($[196] !== t62) {
        t63 = <CopyableContent text={t62} />;
        $[196] = t62;
        $[197] = t63;
      } else {
        t63 = $[197];
      }
      let t64;
      if ($[198] !== t60 || $[199] !== t63) {
        t64 = <p>{t58} {t60},{" "}{t61} {t63}</p>;
        $[198] = t60;
        $[199] = t63;
        $[200] = t64;
      } else {
        t64 = $[200];
      }
      let t65;
      if ($[201] !== resolvedReceiving.routedToSscardio) {
        t65 = resolvedReceiving.routedToSscardio && <p className="text-xs">Routing: <strong>sscardio</strong></p>;
        $[201] = resolvedReceiving.routedToSscardio;
        $[202] = t65;
      } else {
        t65 = $[202];
      }
      let t66;
      if ($[203] !== resolvedReceiving.flags) {
        t66 = resolvedReceiving.flags.length > 0 && <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{resolvedReceiving.flags.map(_temp27)}</ul>;
        $[203] = resolvedReceiving.flags;
        $[204] = t66;
      } else {
        t66 = $[204];
      }
      let t67;
      if ($[205] !== resolvedReceiving.trace) {
        t67 = resolvedReceiving.trace.length > 0 && <details className="text-xs"><summary>Why</summary><ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{resolvedReceiving.trace.map(_temp28)}</ul></details>;
        $[205] = resolvedReceiving.trace;
        $[206] = t67;
      } else {
        t67 = $[206];
      }
      if ($[207] !== t64 || $[208] !== t65 || $[209] !== t66 || $[210] !== t67) {
        t21 = <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">{t57}{t64}{t65}{t66}{t67}</div>;
        $[207] = t64;
        $[208] = t65;
        $[209] = t66;
        $[210] = t67;
        $[211] = t21;
      } else {
        t21 = $[211];
      }
      let t68;
      if ($[212] === Symbol.for("react.memo_cache_sentinel")) {
        t68 = <h4 className="font-semibold text-foreground mb-2">Patient Chart Create</h4>;
        $[212] = t68;
      } else {
        t68 = $[212];
      }
      let t69;
      if ($[213] !== documentId) {
        t69 = <li>Search by document ID: <CopyableContent text={documentId} /></li>;
        $[213] = documentId;
        $[214] = t69;
      } else {
        t69 = $[214];
      }
      let t70;
      if ($[215] !== patientEhrId) {
        t70 = <li>Add patient EHR ID if not already added: <CopyableContent text={patientEhrId} /></li>;
        $[215] = patientEhrId;
        $[216] = t70;
      } else {
        t70 = $[216];
      }
      let t71;
      if ($[217] === Symbol.for("react.memo_cache_sentinel")) {
        t71 = <li>Hit save and proceed to quickview.</li>;
        $[217] = t71;
      } else {
        t71 = $[217];
      }
      let t72;
      if ($[218] !== resolvedReceiving.providerLabel || $[219] !== resolvedReceiving.providerNpi || $[220] !== resolvedReceiving.usualProviderWriteSafe) {
        t72 = resolvedReceiving.usualProviderWriteSafe ? <ResolvedProviderCopyFields providerLabel={resolvedReceiving.providerLabel} providerNpi={resolvedReceiving.providerNpi} writeSafe={true} /> : "Leave unchanged";
        $[218] = resolvedReceiving.providerLabel;
        $[219] = resolvedReceiving.providerNpi;
        $[220] = resolvedReceiving.usualProviderWriteSafe;
        $[221] = t72;
      } else {
        t72 = $[221];
      }
      let t73;
      if ($[222] !== t72) {
        t73 = <li>Usual Provider:{" "}{t72}</li>;
        $[222] = t72;
        $[223] = t73;
      } else {
        t73 = $[223];
      }
      let t74;
      if ($[224] !== referral.referringProvider) {
        t74 = <ProviderCopyFields provider={referral.referringProvider} />;
        $[224] = referral.referringProvider;
        $[225] = t74;
      } else {
        t74 = $[225];
      }
      let t75;
      if ($[226] !== referringProviderSpecialty) {
        t75 = <CopyableContent text={referringProviderSpecialty} />;
        $[226] = referringProviderSpecialty;
        $[227] = t75;
      } else {
        t75 = $[227];
      }
      let t76;
      if ($[228] !== t74 || $[229] !== t75) {
        t76 = <li>Referring Provider: {t74}, Specialty:{" "}{t75}</li>;
        $[228] = t74;
        $[229] = t75;
        $[230] = t76;
      } else {
        t76 = $[230];
      }
      let t77;
      if ($[231] !== isPrimaryCareProvider || $[232] !== referral.referringProvider) {
        t77 = isPrimaryCareProvider && <li>PCP: <ProviderCopyFields provider={referral.referringProvider} /></li>;
        $[231] = isPrimaryCareProvider;
        $[232] = referral.referringProvider;
        $[233] = t77;
      } else {
        t77 = $[233];
      }
      let t78;
      if ($[234] !== referral.referringProvider || $[235] !== referringLocationAddress) {
        t78 = referral.referringProvider && <li>Referring provider address: <CopyableContent text={referringLocationAddress} /></li>;
        $[234] = referral.referringProvider;
        $[235] = referringLocationAddress;
        $[236] = t78;
      } else {
        t78 = $[236];
      }
      let t79;
      if ($[237] !== t73 || $[238] !== t76 || $[239] !== t77 || $[240] !== t78) {
        t79 = <li>Check the Patient Care Team:<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t73}{t76}{t77}{t78}</ul></li>;
        $[237] = t73;
        $[238] = t76;
        $[239] = t77;
        $[240] = t78;
        $[241] = t79;
      } else {
        t79 = $[241];
      }
      let t80;
      if ($[242] !== emergencyContactName) {
        t80 = <li>Name: <CopyableContent text={emergencyContactName} /></li>;
        $[242] = emergencyContactName;
        $[243] = t80;
      } else {
        t80 = $[243];
      }
      let t81;
      if ($[244] !== emergencyContactPhoneNumber) {
        t81 = <li>Phone Number: <CopyableContent text={emergencyContactPhoneNumber} /></li>;
        $[244] = emergencyContactPhoneNumber;
        $[245] = t81;
      } else {
        t81 = $[245];
      }
      let t82;
      if ($[246] !== emergencyContactRelationship) {
        t82 = <li>Relationship to Patient: <CopyableContent text={emergencyContactRelationship} /></li>;
        $[246] = emergencyContactRelationship;
        $[247] = t82;
      } else {
        t82 = $[247];
      }
      let t83;
      if ($[248] !== t80 || $[249] !== t81 || $[250] !== t82) {
        t83 = <li>Check the Emergency Contact<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t80}{t81}{t82}</ul></li>;
        $[248] = t80;
        $[249] = t81;
        $[250] = t82;
        $[251] = t83;
      } else {
        t83 = $[251];
      }
      const t84 = primaryCoverageSummary ?? "N/A";
      let t85;
      if ($[252] !== t84) {
        t85 = <li>Primary: <CopyableContent text={t84} /></li>;
        $[252] = t84;
        $[253] = t85;
      } else {
        t85 = $[253];
      }
      let t86;
      if ($[254] !== secondaryCoverageSummary) {
        t86 = secondaryCoverageSummary && <li>Secondary: <CopyableContent text={secondaryCoverageSummary} /></li>;
        $[254] = secondaryCoverageSummary;
        $[255] = t86;
      } else {
        t86 = $[255];
      }
      let t87;
      let t88;
      if ($[256] !== insuranceStatusDisplay) {
        t87 = <CopyableContent text={insuranceStatusDisplay} />;
        t88 = insuranceStatusDisplay === "Missing Insurance" && <p className="text-xs italic mt-1">Ops needs to call the referring provider to verify insurance.</p>;
        $[256] = insuranceStatusDisplay;
        $[257] = t87;
        $[258] = t88;
      } else {
        t87 = $[257];
        t88 = $[258];
      }
      let t89;
      if ($[259] !== insuranceStatusDisplay) {
        t89 = insuranceStatusDisplay === "Unverified" && <p className="text-xs italic mt-1">Ops needs to call the referring provider to verify insurance.</p>;
        $[259] = insuranceStatusDisplay;
        $[260] = t89;
      } else {
        t89 = $[260];
      }
      let t90;
      if ($[261] !== t87 || $[262] !== t88 || $[263] !== t89) {
        t90 = <li>Eligibility check result: {t87}{t88}{t89}</li>;
        $[261] = t87;
        $[262] = t88;
        $[263] = t89;
        $[264] = t90;
      } else {
        t90 = $[264];
      }
      let t91;
      if ($[265] !== t85 || $[266] !== t86 || $[267] !== t90) {
        t91 = <li>Check the patient insurance<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t85}{t86}{t90}</ul></li>;
        $[265] = t85;
        $[266] = t86;
        $[267] = t90;
        $[268] = t91;
      } else {
        t91 = $[268];
      }
      let t92;
      if ($[269] === Symbol.for("react.memo_cache_sentinel")) {
        t92 = <li>Go to full registration</li>;
        $[269] = t92;
      } else {
        t92 = $[269];
      }
      let t93;
      if ($[270] === Symbol.for("react.memo_cache_sentinel")) {
        t93 = <li>Call and text are YES</li>;
        $[270] = t93;
      } else {
        t93 = $[270];
      }
      let t94;
      if ($[271] !== privacyNoticeOnFile) {
        t94 = privacyNoticeOnFile !== true && <p className="text-xs italic mt-1">{privacyNoticeOnFile === false ? "Privacy is not set on the Athena chart. Set it to YES during review." : "Could not verify Athena privacy. Confirm it is YES during review."}</p>;
        $[271] = privacyNoticeOnFile;
        $[272] = t94;
      } else {
        t94 = $[272];
      }
      let t95;
      if ($[273] !== t94) {
        t95 = <li>Privacy is YES{t94}</li>;
        $[273] = t94;
        $[274] = t95;
      } else {
        t95 = $[274];
      }
      let t96;
      if ($[275] === Symbol.for("react.memo_cache_sentinel")) {
        t96 = <li>If How did you hear about us is blank, set it to PHYSICIAN REFERRAL; leave any existing value unchanged.</li>;
        $[275] = t96;
      } else {
        t96 = $[275];
      }
      let t97;
      if ($[276] !== t95) {
        t97 = <li>Verify Patient information filled in<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t93}{t95}{t96}</ul></li>;
        $[276] = t95;
        $[277] = t97;
      } else {
        t97 = $[277];
      }
      if ($[278] !== t69 || $[279] !== t70 || $[280] !== t79 || $[281] !== t83 || $[282] !== t91 || $[283] !== t97) {
        t22 = <div>{t68}<ol className="list-decimal list-inside space-y-3">{t69}{t70}{t71}{t79}{t83}{t91}{t92}{t97}</ol></div>;
        $[278] = t69;
        $[279] = t70;
        $[280] = t79;
        $[281] = t83;
        $[282] = t91;
        $[283] = t97;
        $[284] = t22;
      } else {
        t22 = $[284];
      }
      if ($[285] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = <h4 className="font-semibold text-foreground mb-2">Referral Processing</h4>;
        $[285] = t15;
      } else {
        t15 = $[285];
      }
      t11 = "list-decimal list-inside space-y-3";
      if ($[286] !== documentId) {
        t12 = <li>Search by document ID: <CopyableContent text={documentId} /></li>;
        $[286] = documentId;
        $[287] = t12;
      } else {
        t12 = $[287];
      }
      let t98;
      if ($[288] !== referringProviderName) {
        t98 = <CopyableContent text={referringProviderName} />;
        $[288] = referringProviderName;
        $[289] = t98;
      } else {
        t98 = $[289];
      }
      const t99 = referral.referringProvider?.npiNumber || NOT_AVAILABLE;
      let t100;
      if ($[290] !== t99) {
        t100 = <CopyableContent text={t99} />;
        $[290] = t99;
        $[291] = t100;
      } else {
        t100 = $[291];
      }
      let t101;
      if ($[292] !== referringLocationAddress) {
        t101 = <CopyableContent text={referringLocationAddress} />;
        $[292] = referringLocationAddress;
        $[293] = t101;
      } else {
        t101 = $[293];
      }
      if ($[294] !== t100 || $[295] !== t101 || $[296] !== t98) {
        t13 = <li>Referring provider: Name: {t98}, NPI:{" "}{t100}, Address:{" "}{t101}</li>;
        $[294] = t100;
        $[295] = t101;
        $[296] = t98;
        $[297] = t13;
      } else {
        t13 = $[297];
      }
      if ($[298] !== resolvedReceiving.providerLabel || $[299] !== resolvedReceiving.providerNpi || $[300] !== resolvedReceiving.providerWriteSafe) {
        t14 = <li>Receiving provider:{" "}<ResolvedProviderCopyFields providerLabel={resolvedReceiving.providerLabel} providerNpi={resolvedReceiving.providerNpi} writeSafe={resolvedReceiving.providerWriteSafe} /></li>;
        $[298] = resolvedReceiving.providerLabel;
        $[299] = resolvedReceiving.providerNpi;
        $[300] = resolvedReceiving.providerWriteSafe;
        $[301] = t14;
      } else {
        t14 = $[301];
      }
      t9 = "Patient Type: ";
      if ($[302] !== patientLabel) {
        t10 = <CopyableContent text={patientLabel} />;
        $[302] = patientLabel;
        $[303] = t10;
      } else {
        t10 = $[303];
      }
      t7 = "ml-4 mt-1 space-y-1 list-disc list-inside text-xs";
      t8 = sortedPastAppointments.length === 0 ? <li>No appointment history found</li> : sortedPastAppointments.map(_temp29);
    }
    $[12] = athenaData?.coverages;
    $[13] = athenaData?.patientDetails;
    $[14] = bucketLabel;
    $[15] = coverageReadback;
    $[16] = dateOfReferral;
    $[17] = documentId;
    $[18] = documentUploadLabels;
    $[19] = eligibilityDisplay;
    $[20] = emergencyContactName;
    $[21] = emergencyContactPhoneNumber;
    $[22] = emergencyContactRelationship;
    $[23] = followThroughEligible;
    $[24] = insuranceRequirements;
    $[25] = internalNoteValue;
    $[26] = isPrimaryCareProvider;
    $[27] = pastAppointments;
    $[28] = patient;
    $[29] = patientEhrId;
    $[30] = patientLabel;
    $[31] = pendingApproval;
    $[32] = primaryCareProviderAlreadyPresent;
    $[33] = primaryCoverageSummary;
    $[34] = privacyNoticeOnFile;
    $[35] = receivingAssignmentConfirmed;
    $[36] = receivingProviderName;
    $[37] = referral.diagnosisList;
    $[38] = referral.id;
    $[39] = referral.primaryCoverage;
    $[40] = referral.reasonForReferral;
    $[41] = referral.receivingLocation?.name;
    $[42] = referral.receivingProvider;
    $[43] = referral.referringLocation?.name;
    $[44] = referral.referringProvider;
    $[45] = referralAuthorization;
    $[46] = referringLocationAddress;
    $[47] = referringProviderName;
    $[48] = referringProviderSpecialty;
    $[49] = resolvedReceiving;
    $[50] = secondaryCoverageSummary;
    $[51] = task;
    $[52] = taskSteps;
    $[53] = unresolvedUpcomingProviders;
    $[54] = upcomingAppointmentProviderSummary;
    $[55] = upcomingAppointments;
    $[56] = view;
    $[57] = T0;
    $[58] = dataSourceEntries;
    $[59] = hasMissingInformation;
    $[60] = missingFields;
    $[61] = t10;
    $[62] = t11;
    $[63] = t12;
    $[64] = t13;
    $[65] = t14;
    $[66] = t15;
    $[67] = t16;
    $[68] = t17;
    $[69] = t18;
    $[70] = t19;
    $[71] = t20;
    $[72] = t21;
    $[73] = t22;
    $[74] = t23;
    $[75] = t24;
    $[76] = t25;
    $[77] = t7;
    $[78] = t8;
    $[79] = t9;
  } else {
    T0 = $[57];
    dataSourceEntries = $[58];
    hasMissingInformation = $[59];
    missingFields = $[60];
    t10 = $[61];
    t11 = $[62];
    t12 = $[63];
    t13 = $[64];
    t14 = $[65];
    t15 = $[66];
    t16 = $[67];
    t17 = $[68];
    t18 = $[69];
    t19 = $[70];
    t20 = $[71];
    t21 = $[72];
    t22 = $[73];
    t23 = $[74];
    t24 = $[75];
    t25 = $[76];
    t7 = $[77];
    t8 = $[78];
    t9 = $[79];
  }
  if (t25 !== Symbol.for("react.early_return_sentinel")) {
    return t25;
  }
  let t26;
  if ($[304] !== t7 || $[305] !== t8) {
    t26 = <ul className={t7}>{t8}</ul>;
    $[304] = t7;
    $[305] = t8;
    $[306] = t26;
  } else {
    t26 = $[306];
  }
  let t27;
  if ($[307] !== t10 || $[308] !== t26 || $[309] !== t9) {
    t27 = <li>{t9}{t10}{t26}</li>;
    $[307] = t10;
    $[308] = t26;
    $[309] = t9;
    $[310] = t27;
  } else {
    t27 = $[310];
  }
  let t28;
  if ($[311] !== dateOfReferral) {
    t28 = <li>Date: <CopyableContent text={dateOfReferral} /></li>;
    $[311] = dateOfReferral;
    $[312] = t28;
  } else {
    t28 = $[312];
  }
  let t29;
  if ($[313] !== internalNoteValue) {
    t29 = <CopyableContent text={internalNoteValue} />;
    $[313] = internalNoteValue;
    $[314] = t29;
  } else {
    t29 = $[314];
  }
  let t30;
  if ($[315] !== flags) {
    t30 = flags.length > 0 && <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{flags.map(_temp30)}</ul>;
    $[315] = flags;
    $[316] = t30;
  } else {
    t30 = $[316];
  }
  let t31;
  if ($[317] !== t29 || $[318] !== t30) {
    t31 = <li>Internal Note: {t29}{t30}</li>;
    $[317] = t29;
    $[318] = t30;
    $[319] = t31;
  } else {
    t31 = $[319];
  }
  let t32;
  if ($[320] !== schedulingPriority) {
    t32 = <li>Urgency: <CopyableContent text={schedulingPriority} /></li>;
    $[320] = schedulingPriority;
    $[321] = t32;
  } else {
    t32 = $[321];
  }
  let t33;
  if ($[322] !== bucketLabel) {
    t33 = bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? <>{NO_CONFIDENT_BUCKET_SUGGESTION}</> : <>Suggested Bucket: <CopyableContent text={SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel} /></>;
    $[322] = bucketLabel;
    $[323] = t33;
  } else {
    t33 = $[323];
  }
  let t34;
  if ($[324] !== bucketLabel || $[325] !== decisionTrace) {
    t34 = decisionTrace.length > 0 && (bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{decisionTrace.map(_temp31)}</ul> : <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs"><li>{decisionTrace[decisionTrace.length - 1].label}{" "}<strong>{decisionTrace[decisionTrace.length - 1].outcome}</strong></li></ul>);
    $[324] = bucketLabel;
    $[325] = decisionTrace;
    $[326] = t34;
  } else {
    t34 = $[326];
  }
  let t35;
  if ($[327] !== t33 || $[328] !== t34) {
    t35 = <li>{t33}{t34}</li>;
    $[327] = t33;
    $[328] = t34;
    $[329] = t35;
  } else {
    t35 = $[329];
  }
  let t36;
  if ($[330] !== actionNote) {
    t36 = <li>Action note: <CopyableContent text={actionNote} /></li>;
    $[330] = actionNote;
    $[331] = t36;
  } else {
    t36 = $[331];
  }
  let t37;
  if ($[332] !== internalNoteValue) {
    t37 = <li>Go to quickview and add the internal note: <CopyableContent text={internalNoteValue} /></li>;
    $[332] = internalNoteValue;
    $[333] = t37;
  } else {
    t37 = $[333];
  }
  let t38;
  if ($[334] !== t11 || $[335] !== t12 || $[336] !== t13 || $[337] !== t14 || $[338] !== t27 || $[339] !== t28 || $[340] !== t31 || $[341] !== t32 || $[342] !== t35 || $[343] !== t36 || $[344] !== t37) {
    t38 = <ol className={t11}>{t12}{t13}{t14}{t27}{t28}{t31}{t32}{t35}{t36}{t37}</ol>;
    $[334] = t11;
    $[335] = t12;
    $[336] = t13;
    $[337] = t14;
    $[338] = t27;
    $[339] = t28;
    $[340] = t31;
    $[341] = t32;
    $[342] = t35;
    $[343] = t36;
    $[344] = t37;
    $[345] = t38;
  } else {
    t38 = $[345];
  }
  let t39;
  if ($[346] !== t15 || $[347] !== t38) {
    t39 = <div>{t15}{t38}</div>;
    $[346] = t15;
    $[347] = t38;
    $[348] = t39;
  } else {
    t39 = $[348];
  }
  let t40;
  let t41;
  if ($[349] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <h4 className="font-semibold text-foreground mb-2">Doc Upload</h4>;
    t41 = <p className="mb-1">Verify the following documents were uploaded:</p>;
    $[349] = t40;
    $[350] = t41;
  } else {
    t40 = $[349];
    t41 = $[350];
  }
  let t42;
  if ($[351] !== documentUploadLabels) {
    t42 = documentUploadLabels.length === 0 ? <li>N/A</li> : documentUploadLabels.map(_temp32);
    $[351] = documentUploadLabels;
    $[352] = t42;
  } else {
    t42 = $[352];
  }
  let t43;
  if ($[353] !== t42) {
    t43 = <div>{t40}{t41}<ul className="ml-4 space-y-1 list-disc list-inside">{t42}</ul></div>;
    $[353] = t42;
    $[354] = t43;
  } else {
    t43 = $[354];
  }
  let t44;
  let t45;
  if ($[355] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <h4 className="font-semibold text-foreground mb-2">Missing Information</h4>;
    t45 = <p className="font-medium text-foreground">Step 1: Missing Information Check</p>;
    $[355] = t44;
    $[356] = t45;
  } else {
    t44 = $[355];
    t45 = $[356];
  }
  let t46;
  if ($[357] !== hasMissingInformation || $[358] !== missingFields) {
    t46 = hasMissingInformation ? `Missing: ${missingFields.map(_temp33).join(", ")}` : "No missing-information letter needed.";
    $[357] = hasMissingInformation;
    $[358] = missingFields;
    $[359] = t46;
  } else {
    t46 = $[359];
  }
  let t47;
  if ($[360] !== t46) {
    t47 = <p className="mb-3">{t46}</p>;
    $[360] = t46;
    $[361] = t47;
  } else {
    t47 = $[361];
  }
  let t48;
  if ($[362] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <p className="font-medium text-foreground">Step 2: Data Source Checklist</p>;
    $[362] = t48;
  } else {
    t48 = $[362];
  }
  let t49;
  if ($[363] !== dataSourceEntries) {
    t49 = <ul className="ml-4 mb-3 space-y-1 list-disc list-inside">{dataSourceEntries.map(_temp34)}</ul>;
    $[363] = dataSourceEntries;
    $[364] = t49;
  } else {
    t49 = $[364];
  }
  let t50;
  if ($[365] !== dateOfReferral || $[366] !== hasMissingInformation || $[367] !== referringProviderName) {
    t50 = hasMissingInformation && <ol className="list-decimal list-inside space-y-2"><li>Verify the Auto-Generated document was added to the patient chart</li><li>Send the document out.<ul className="ml-4 mt-1 space-y-2 list-none"><li>a. Chart Export - sending to referring provider<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>Click the hamburger menu (3 dashes) → select Chart Export.</li><li>Click the + button next to Admin Documents.</li><li>Find the "Request for Additional Information" doc and move it to the right-hand side.</li><li>Click Export Chart Sections → send to <CopyableContent text={referringProviderName} /></li></ul></li><li>c. Fill out export details<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>Type <CopyableContent text={referringProviderName} /></li><li>Confirm method is set to Athena Fax and the fax number has populated.</li><li>Attention box: <CopyableContent text={referringProviderName} /></li><li>Note field: "Request for missing information."</li><li>Confirm Submit, then Save.</li></ul></li><li>d. Add Note to Patient Chart<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>"Valerie Health: Missing Information Requested <CopyableContent text={dateOfReferral} />"</li></ul></li></ul></li></ol>;
    $[365] = dateOfReferral;
    $[366] = hasMissingInformation;
    $[367] = referringProviderName;
    $[368] = t50;
  } else {
    t50 = $[368];
  }
  let t51;
  if ($[369] !== t47 || $[370] !== t49 || $[371] !== t50) {
    t51 = <div>{t44}{t45}{t47}{t48}{t49}{t50}</div>;
    $[369] = t47;
    $[370] = t49;
    $[371] = t50;
    $[372] = t51;
  } else {
    t51 = $[372];
  }
  let t52;
  if ($[373] !== t16 || $[374] !== t17 || $[375] !== t18 || $[376] !== t19 || $[377] !== t20 || $[378] !== t21 || $[379] !== t22 || $[380] !== t39 || $[381] !== t43 || $[382] !== t51) {
    t52 = <div className={t16}>{t17}{t18}{t19}{t20}{t21}{t22}{t39}{t43}{t51}</div>;
    $[373] = t16;
    $[374] = t17;
    $[375] = t18;
    $[376] = t19;
    $[377] = t20;
    $[378] = t21;
    $[379] = t22;
    $[380] = t39;
    $[381] = t43;
    $[382] = t51;
    $[383] = t52;
  } else {
    t52 = $[383];
  }
  let t53;
  if ($[384] !== T0 || $[385] !== t23 || $[386] !== t52) {
    t53 = <T0 collapsibleTitle={t23}>{t52}</T0>;
    $[384] = T0;
    $[385] = t23;
    $[386] = t52;
    $[387] = t53;
  } else {
    t53 = $[387];
  }
  let t54;
  if ($[388] !== referral.id || $[389] !== task.id) {
    t54 = <ReferralEligibilityCard referralId={referral.id} taskId={task.id} />;
    $[388] = referral.id;
    $[389] = task.id;
    $[390] = t54;
  } else {
    t54 = $[390];
  }
  let t55;
  if ($[391] !== athenaData || $[392] !== referral) {
    t55 = <AthenaReferralCards referral={referral} athenaData={athenaData} />;
    $[391] = athenaData;
    $[392] = referral;
    $[393] = t55;
  } else {
    t55 = $[393];
  }
  let t56;
  if ($[394] !== t24 || $[395] !== t53 || $[396] !== t54 || $[397] !== t55) {
    t56 = <div className={t24}>{t53}{t54}{t55}</div>;
    $[394] = t24;
    $[395] = t53;
    $[396] = t54;
    $[397] = t55;
    $[398] = t56;
  } else {
    t56 = $[398];
  }
  return t56;
}
function _temp34(entry_2) {
  return <li key={entry_2.label}>{entry_2.label}: <CopyableContent text={entry_2.value} /> ({entry_2.source})</li>;
}
function _temp33(entry_1) {
  return entry_1.label;
}
function _temp32(label) {
  return <li key={label}><CopyableContent text={label} /></li>;
}
function _temp31(entry_0, index_3) {
  return <li key={`${entry_0.label}-${index_3}`}>{entry_0.label} <strong>{entry_0.outcome}</strong></li>;
}
function _temp30(flag_0, index_2) {
  return <li key={index_2}>{flag_0}</li>;
}
function _temp29(appointment) {
  return <li key={appointment.id}>{formatAppointmentHistoryEntry(appointment)}</li>;
}
function _temp28(entry, index_1) {
  return <li key={index_1}>{entry}</li>;
}
function _temp27(flag, index_0) {
  return <li key={index_0}>{flag}</li>;
}
function _temp26(value_0, index, values) {
  return values.indexOf(value_0) === index;
}
function _temp25(value) {
  return Boolean(value);
}
function _temp24(item) {
  return item.diagnosisText;
}
function _temp23(a, b) {
  return (a.order ?? 99) - (b.order ?? 99);
}